/**
 * Section List Panel. Renders the current page's sections as an ordered,
 * reorderable list in the left panel. Supports drag/drop, move up/down,
 * duplicate, delete, rename, hover-to-highlight, click-to-scroll,
 * variant popover, and SVG asset panel.
 */

import { store } from '../core/state.js';
import { eventBus } from '../core/events.js';
import { reportError } from '../core/report-error.js';
import { bridge } from '../editing/bridge.js';
import * as sectionManager from '../sections/section-manager.js';
import * as sectionService from './command-bar/section-command-service.js';
import { injectEditableAttributes } from '../renderer/iframe-renderer.js';
import {
  setActiveSvgEdit,
  setActiveImageEdit,
  openSvgLibraryForSwap,
  swapSvgToImage
} from '../editing/image-editor.js';
import { openReplaceDialog } from '../editing/replace-dispatcher.js';
import { animateIn, animateOut, landed, consumeHints, prefersReducedMotion } from './feedback.js';
import {
  gridKeyNav,
  copySectionById,
  hasSectionClipboard,
  getSectionClipboard,
  pasteSectionAt,
  getSelectedInstanceId
} from './shell.js';
import { createButton, createCheckbox } from './components.js';
import { selection } from '../core/selection-store.js';
import { setVisibleOrderProvider } from './selection-controller.js';
import {
  resolveClickIntent,
  resolveRowKeyIntent,
  extendTargetFromLead,
  nextFocusTarget,
  summarizeSelection,
  selectionAnnouncement
} from './section-list-interaction.js';
import { ANIM_MS, debounce } from '../shared/timing.js';
import { extractSectionCssBackgroundImages } from '../shared/css-background-images.js';
import { loadJSON } from '../core/asset-loader.js';
import { getCategoryMap } from '../shared/section-display-name.js';
import {
  getSafeAreaInsets,
  getViewportHeight,
  isPhoneViewport,
  onViewportChange
} from '../shared/viewport-service.js';
import { checkNavHtmlEligibility } from '../shared/mobile-menu-contract.js';
import {
  describeInsertPosition,
  nextCategoryAfter,
  resolveSectionName,
  shouldUseTemplateSectionThumbnail,
  stitchHandle
} from '../shared/section-categories.js';
import { sectionFactsFor } from '../shared/section-facts.js';
import { disposeFindings } from '../shared/finding-disposition.js';
import { computeAutoScrollDelta } from '../shared/touch-geometry.js';
import {
  applyThumbnail,
  catalogSectionThumbnail,
  templateSectionThumbnail
} from '../shared/thumbnail-url.js';
import { scheduleSummary } from '../shared/schedule-state.js';

/** The injected parse seam for section-facts (memoized there by html string). */
function parseSectionHtml(html) {
  return new DOMParser().parseFromString(html, 'text/html').body;
}

/**
 * Rendered widths for this file's two thumbnail surfaces. Both are far below
 * the 400w derivative, so the browser never reaches for the 960w candidate:
 * `.pb-sl-thumb` is 88px wide at 3/2 (SS-3 — sized so a dark section is
 * distinguishable from the one below it), and a variant popover card fills a
 * ~140px track.
 */
const ROW_THUMB_SIZES = '88px';

// ── Inline SVG icons (14×14 viewBox) ──

const ICON_GRIP = `<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="5" cy="3" r="1"/><circle cx="9" cy="3" r="1"/><circle cx="5" cy="7" r="1"/><circle cx="9" cy="7" r="1"/><circle cx="5" cy="11" r="1"/><circle cx="9" cy="11" r="1"/></svg>`;
const ICON_PHONE = `<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="1.5" width="6" height="11" rx="1.2"/><line x1="6.5" y1="10.5" x2="7.5" y2="10.5"/></svg>`;
const ICON_CHEVRON_UP = `<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l4-4 4 4"/></svg>`;
const ICON_CHEVRON_DOWN = `<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 5l4 4 4-4"/></svg>`;
const ICON_DUPLICATE = `<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="5" width="7" height="7" rx="1"/><path d="M2 9V3a1 1 0 0 1 1-1h6"/></svg>`;
const ICON_TRASH = `<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2 4h10M5 4V2.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 .5.5V4m1.5 0v7.5a1 1 0 0 1-1 1h-5a1 1 0 0 1-1-1V4"/></svg>`;
const ICON_IMAGE = `<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="2" width="12" height="10" rx="1"/><circle cx="4.5" cy="5.5" r="1.5"/><path d="M13 9l-3-3-4 4-2-2-3 3"/></svg>`;
const ICON_LOCK = `<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="6" width="8" height="6" rx="1"/><path d="M5 6V4a2 2 0 0 1 4 0v2"/></svg>`;
const ICON_THUMB_ERROR = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5-5 5"/><line x1="3" y1="3" x2="21" y2="21"/></svg>`;
const ICON_COPY = `<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="4" width="8" height="8" rx="1"/><path d="M2 10V3a1 1 0 0 1 1-1h7"/></svg>`;
const ICON_CLIPBOARD = `<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="8" height="9" rx="1"/><path d="M5 2.5h4a.5.5 0 0 1 .5.5V4h-5V3a.5.5 0 0 1 .5-.5z"/></svg>`;
const ICON_DOTS_VERTICAL = `<svg viewBox="0 0 14 14" fill="currentColor" stroke="none" aria-hidden="true"><circle cx="7" cy="3" r="1.2"/><circle cx="7" cy="7" r="1.2"/><circle cx="7" cy="11" r="1.2"/></svg>`;
const ICON_PALETTE = `<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 1.5C3.5 1.5 1 4 1 7c0 2.5 2 4 4 4 .8 0 1-.6 1-1.2 0-.4-.2-.7-.2-1 0-.6.5-1 1.1-1H8c2.2 0 4-1.5 4-3.5 0-1.7-1.6-3-5-3.8z"/><circle cx="3.5" cy="6.5" r="0.6" fill="currentColor"/><circle cx="6" cy="3.8" r="0.6" fill="currentColor"/><circle cx="9.5" cy="5" r="0.6" fill="currentColor"/></svg>`;
const ICON_CLOCK = `<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="7" cy="7" r="5.25"/><path d="M7 4v3.25l2.25 1.25"/></svg>`;
const ICON_RENAME = `<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2 10.5V12h1.5l7.1-7.1-1.5-1.5L2 10.5z"/><path d="M8.4 2.1l1.5-1.5 1.5 1.5-1.5 1.5z"/></svg>`;
// SS-3: the row's attention flag (open audit findings for this section).
const ICON_FLAG = `<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 5.2v2.6M7 10h.01"/><path d="M6 1.8 1.2 10.4a1.2 1.2 0 0 0 1 1.8h9.6a1.2 1.2 0 0 0 1-1.8L8 1.8a1.2 1.2 0 0 0-2 0z"/></svg>`;

export class SectionList {
  /**
   * @param {HTMLElement} container
   * @param {{ mode?: 'full' | 'sidebar' }} opts
   *   - 'full' (default): renders tab shell + section list + theme/properties mount points
   *   - 'sidebar': renders section list body only (no tabs, no mount points — NavSidebar owns those)
   */
  constructor(container, opts = {}) {
    this._container = container;
    this._mode = opts.mode || 'full';
    this._sections = []; // section-index.json data
    this._categoryMap = {}; // category-map.json data
    this._indexMap = new Map(); // catalogKey → { thumbnail, description, category }
    this._selectedInstanceId = null;
    this._activeVisibleInstanceId = null;
    this._hoverCooldownActive = false;
    this._renderDebounce = null;
    this._activeMediaPanel = null;
    this._pendingSectionOps = 0;
    this._latestSectionOp = 'add';
    this._addModal = null; // lazy-loaded AddSectionModal
    this._loadPromise = null;
    this._addModalLoading = 0;
    this._searchQuery = ''; // panel-top-zone search filter (Phase 1b)
    this._rowsRenderSignature = '';
    // ── Multi-select (16A1) ──
    this._selectingMode = false; // header "Select" toggle — the touch path
    this._rowChecks = new Map(); // instanceId → setChecked (kit checkbox handles)
    this._announceTimer = null; // 120ms trailing debounce for the live region
    this._bulkBarEls = null; // { bar, count, clearBtn } — built once, mutated in place

    // Range gestures and Cmd+A resolve against what the user can SEE — rows
    // minus filtered and shared ones. The controller falls back to state order
    // when this returns null (list not mounted / empty).
    setVisibleOrderProvider(() => {
      const order = this._visibleCandidateOrder();
      return order.length ? order : null;
    });

    this._render();
    // Initial paint (previously driven by _load()'s completion). Section-index
    // (2.2MB) is now loaded lazily — this render shows the empty state for a
    // blank project (no fetch), and `_renderRows()` triggers the memoized
    // `_load()` the first time there ARE sections to show. The AddSectionModal
    // path also awaits `_load()` on open. A truly-empty project fetches nothing
    // at boot.
    this._renderRows();
    this._listen();
  }

  // ── Data loading ──

  async _load() {
    this._loadPromise =
      this._loadPromise ||
      (async () => {
        try {
          const [sections, categoryMap] = await Promise.all([
            loadJSON('data/section-index.json'),
            getCategoryMap()
          ]);
          this._sections = sections;
          this._categoryMap = categoryMap;

          for (const s of this._sections) {
            this._indexMap.set(s.catalogKey, {
              thumbnail: s.thumbnail,
              description: s.description,
              category: s.category
            });
          }

          this._renderRows();
        } catch (err) {
          reportError('SectionList', err, {
            user: 'toast',
            message: 'Failed to load section data',
            detail: 'section index/category map load failed',
            id: 'section-list-load-failed'
          });
          this._bodyEl.innerHTML = `<div class="pb-empty-state"><span>Failed to load section data</span></div>`;
        }
      })();
    return this._loadPromise;
  }

  // ── Event subscriptions ──

  _listen() {
    eventBus.on('state-changed', (state) => {
      const nextSignature = this._buildRowsRenderSignature(state);
      if (nextSignature === this._rowsRenderSignature) return;
      this._rowsRenderSignature = nextSignature;
      clearTimeout(this._renderDebounce);
      this._renderDebounce = setTimeout(() => this._renderRows(), 50);
    });

    // SS-3: a section that needs attention says so on its row, not in a panel
    // three clicks away. Findings already carry location.instanceId; the flag
    // is derived from OPEN findings only (post-disposeFindings, same rule as
    // the tab dot). The report is NOT store state, so the render signature
    // gains an audit key below — without it the row would never repaint when
    // findings change.
    eventBus.on('pre-publish-audit:report', (report = {}) => {
      this._auditReport = report;
      const nextSignature = this._buildRowsRenderSignature();
      if (nextSignature === this._rowsRenderSignature) return;
      this._rowsRenderSignature = nextSignature;
      clearTimeout(this._renderDebounce);
      this._renderDebounce = setTimeout(() => this._renderRows(), 50);
    });

    eventBus.on('clipboard:changed', () => {
      this._updateClipboardChip();
      // Re-render rows so insert-marker Paste buttons appear/disappear.
      this._renderRows();
    });

    // Command-palette "Rename Section" — same inline editor the F2 row
    // shortcut uses. Page sections only; shared rows have a fixed name.
    // MM-3: the property panel (and anything else) opens the fragment picker
    // through this event — the modal instance lives here.
    eventBus.on('mobile-menu:open-picker', () => {
      void this.openMobileMenuPicker();
    });

    eventBus.on('cmd:rename-section', () => {
      const instanceId = getSelectedInstanceId();
      if (!instanceId || sectionManager.getSharedType(instanceId)) return;
      this._collapseToRow(instanceId); // single-target rule (16A1)
      const label = this._bodyEl?.querySelector(`[data-instance-id="${instanceId}"] .pb-sl-label`);
      if (label) this._startRename(label, instanceId);
    });

    // When the canvas empty stage shows or hides, the sidebar empty-state
    // swaps between the full hero (stage absent) and a compact mini variant
    // (stage active — avoids two empty states 12 inches apart).
    eventBus.on('stage:active-changed', () => {
      this._renderRows();
    });

    // Phase 1b — panel-top-zone search filter. Only react when the Sections
    // tab fired the event (other tabs share the same channel).
    eventBus.on('panel:search', ({ tab, query }) => {
      if (tab !== 'sections') return;
      this._searchQuery = (query || '').trim().toLowerCase();
      this._applySearch();
    });

    // Switching to any non-Sections tab clears our filter — otherwise a stale
    // filter would still hide rows when the user returns to the Sections tab.
    eventBus.on('panel:tab-changed', ({ tab }) => {
      if (tab === 'sections') return;
      this._closeFloatingMenus({ immediate: true, restoreFocus: false });
      if (!this._searchQuery) return;
      this._searchQuery = '';
      this._applySearch();
    });

    // Enter key in the search input — scroll-into-view + select first match.
    eventBus.on('panel:search-submit', ({ tab, query }) => {
      if (tab !== 'sections') return;
      const q = (query || '').trim().toLowerCase();
      if (!q) return;
      this._searchQuery = q;
      this._applySearch();
      const firstVisible = this._bodyEl?.querySelector('.pb-sl-row:not(.pb-sl-row-filtered)');
      if (!firstVisible) return;
      const instanceId = firstVisible.dataset.instanceId;
      if (!instanceId) return;
      this._selectedInstanceId = instanceId;
      this._highlightRow(instanceId);
      bridge.send('scroll-to-section', { instanceId });
      eventBus.emit('iframe:section-clicked', { instanceId });
    });

    // Pre-delete slide-out animation
    eventBus.on('feedback:pre-delete', ({ instanceId, done }) => {
      const row = this._bodyEl?.querySelector(`[data-instance-id="${instanceId}"]`);
      if (!row || prefersReducedMotion()) {
        done();
        return;
      }
      animateOut(row).then(done);
    });

    // A bulk delete that fails revalidation AFTER its animation played (a
    // peer deleted a target mid-confirm) commits nothing — so no state-changed
    // rebuild will resurrect the rows. Un-animate them explicitly (16A2b).
    eventBus.on('feedback:pre-delete-restore', ({ instanceIds = [] } = {}) => {
      for (const instanceId of instanceIds) {
        this._bodyEl
          ?.querySelector(`[data-instance-id="${instanceId}"]`)
          ?.classList.remove('pb-animate-out');
      }
    });

    // Programmatic section selection (used by post-template-load and post-add flows)
    eventBus.on('section:select', ({ instanceId }) => {
      if (!instanceId) return;
      if (this._selectedInstanceId === instanceId) return;
      this._selectedInstanceId = instanceId;
      this._setActiveVisibleInstance(instanceId);
      this._highlightRow(instanceId);
      bridge.send('scroll-to-section', { instanceId });
      eventBus.emit('iframe:section-clicked', { instanceId });
    });

    eventBus.on('iframe:section-clicked', (data) => {
      this._closeFloatingMenus({ immediate: true, restoreFocus: false });
      this._selectedInstanceId = data.instanceId;
      this._setActiveVisibleInstance(data.instanceId);
      this._highlightRow(data.instanceId);
      if (this._mode === 'full') {
        this._setPropertiesTabVisible(true);
        // Auto-switch to Properties tab when a section is clicked
        if (this._activeTab === 'sections') {
          this._switchTab('properties');
        }
      }
    });

    eventBus.on('section-deselected', () => {
      this._closeFloatingMenus({ immediate: true, restoreFocus: false });
      this._selectedInstanceId = null;
      this._highlightRow(null);
      if (this._mode === 'full') {
        if (this._activeTab === 'properties') this._switchTab('sections');
        this._setPropertiesTabVisible(false);
      }
    });

    eventBus.on('iframe:canvas-clicked', () => {
      this._closeFloatingMenus({ immediate: true, restoreFocus: false });
    });

    // ── Multi-select (16A1): the store's derived event drives ALL multi paint.
    // The legacy single-select listeners above keep their exact behaviour; this
    // handler is additive — it owns the co-selected row classes, checkboxes,
    // bulk bar, roving focus and the live region.
    eventBus.on('selection:changed', (change) => {
      this._syncSelectionUI(change);
    });

    // Cmd+A lands here so the Sections tab is showing what just got selected.
    eventBus.on('panel:focus-sections', () => {
      if (this._mode === 'full') this._switchTab('sections');
    });

    eventBus.on('sidebar:state-changed', ({ open } = {}) => {
      if (open === false) {
        this._closeFloatingMenus({ immediate: true, restoreFocus: false });
      }
    });

    // Editing events also reveal the Properties tab (full mode only)
    if (this._mode === 'full') {
      eventBus.on('editing:text-started', () => this._setPropertiesTabVisible(true));
      eventBus.on('editing:image-selected', () => this._setPropertiesTabVisible(true));
      eventBus.on('editing:svg-selected', () => this._setPropertiesTabVisible(true));
    }

    eventBus.on('editing:image-replaced', ({ instanceId } = {}) => {
      this._refreshOpenImagePanel(instanceId);
    });
    eventBus.on('editing:image-removed', ({ instanceId } = {}) => {
      this._refreshOpenImagePanel(instanceId);
    });

    eventBus.on('section:async-status', ({ loading, operation }) => {
      if (loading) {
        this._pendingSectionOps++;
        this._latestSectionOp = operation || this._latestSectionOp;
      } else {
        this._pendingSectionOps = Math.max(0, this._pendingSectionOps - 1);
      }
      this._updateInlineLoading();
    });

    eventBus.on('iframe:section-positions', (data) => {
      this._syncActiveVisibleSection(data);
    });

    // Suppress hover-induced divider jitter while scrolling (guard against duplicate binding)
    if (!this._scrollListenerBound && this._bodyEl) {
      this._scrollListenerBound = true;
      this._endScroll = debounce(() => this._bodyEl?.classList.remove('pb-sl-scrolling'), 150);
      this._bodyEl.addEventListener(
        'scroll',
        () => {
          this._bodyEl.classList.add('pb-sl-scrolling');
          this._endScroll();
        },
        { passive: true }
      );
    }
  }

  _closeFloatingMenus({ immediate = true, restoreFocus = false } = {}) {
    this._cancelThumbPreview();
    this._closeRowActionsMenu({ restoreFocus });
    this._closeMediaPanel({ immediate });
  }

  _getSectionByInstance(instanceId) {
    if (!instanceId) return null;
    const state = store.getState();
    const nav = state.sharedSections?.navigation;
    if (nav?.instanceId === instanceId) return nav;
    const footer = state.sharedSections?.footer;
    if (footer?.instanceId === instanceId) return footer;
    const page = state.pages?.[state.activePage];
    return (page?.sections || []).find((section) => section.instanceId === instanceId) || null;
  }

  _refreshOpenImagePanel(instanceId = null) {
    const panelState = this._activeMediaPanel;
    if (!panelState || panelState.type !== 'img') return;
    if (instanceId && panelState.instanceId !== instanceId) return;
    const section = this._getSectionByInstance(panelState.instanceId);
    const rowEl = panelState.rowEl;
    if (!section || !rowEl?.isConnected) {
      this._closeMediaPanel({ immediate: true });
      return;
    }
    this._closeMediaPanel({ immediate: true });
    this._toggleImagePanel(rowEl, section);
  }

  // ── Shell render ──

  _render() {
    if (this._mode === 'sidebar') {
      this._renderSidebarMode();
    } else {
      this._renderFullMode();
    }
  }

  /** Sidebar mode: section list body only — no tabs, no mount points. */
  _renderSidebarMode() {
    this._container.innerHTML = `
      <div class="pb-sl pb-sl-sidebar-mode">
        <div class="pb-sl-header">
          <span>Sections</span>
          <div class="pb-sl-header-actions">
            <span class="pb-sl-inline-loading" id="pb-sl-inline-loading" hidden>
              <span class="pb-sl-inline-loading-spinner" aria-hidden="true"></span>
              <span class="pb-sl-inline-loading-text">Loading\u2026</span>
            </span>
            <button class="pb-btn pb-btn-ghost pb-sl-select-toggle" data-testid="sl-select-toggle" aria-pressed="false" data-tooltip="Select multiple sections">Select</button>
            <button class="pb-btn pb-btn-primary pb-sl-add-btn" data-testid="sl-add-section" data-tooltip="Add section">+ Add Section</button>
          </div>
        </div>
        <div class="pb-sl-bulk-bar" data-testid="sl-bulk-bar" role="toolbar" aria-label="Selected sections" aria-controls="pb-sl-body-list" hidden></div>
        <label class="pb-sl-search-wrap">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
          <input class="pb-sl-search" type="text" placeholder="Find a section…" aria-label="Find a section on this page" autocomplete="off" data-testid="sl-search" />
          <button type="button" class="pb-sl-search-clear" aria-label="Clear search" data-testid="sl-search-clear" hidden>×</button>
        </label>
        <div class="pb-sl-body" id="pb-sl-body-list" role="list" aria-label="Sections"></div>
      </div>
    `;

    this._bodyEl = this._container.querySelector('.pb-sl-body');
    this._container
      .querySelector('[data-testid="sl-add-section"]')
      .addEventListener('click', () => this._openAddModal(null));
    this._activeTab = 'sections';
    this._wireSelectionChrome();
    this._wireSearchField();
    this._updateClipboardChip();
  }

  /**
   * SS-4 — the input that was always missing. The whole filter backend
   * (`panel:search` listeners, `.pb-sl-row-filtered` CSS, filter-aware
   * selection, "N hidden by filter" disclosure, bulk-delete hiddenNames) has
   * shipped complete since 16A1 — and NOTHING emitted the event, because
   * panel-top-zone's input was removed in favour of the command palette,
   * which indexes the catalog of sections to ADD, never this page's own.
   * This field is the missing emitter; the listeners are untouched.
   */
  _wireSearchField() {
    const input = this._container.querySelector('[data-testid="sl-search"]');
    const clear = this._container.querySelector('[data-testid="sl-search-clear"]');
    if (!input) return;
    const emit = () => {
      clear.hidden = !input.value;
      eventBus.emit('panel:search', { tab: 'sections', query: input.value });
    };
    input.addEventListener('input', emit);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        eventBus.emit('panel:search-submit', { tab: 'sections', query: input.value });
      } else if (e.key === 'Escape') {
        // Escape unwinds one layer: clear the query first; an already-empty
        // field hands Escape back to the global chain (selection clearing).
        if (input.value) {
          e.stopPropagation();
          input.value = '';
          emit();
        } else {
          input.blur();
        }
      }
    });
    clear.addEventListener('click', () => {
      input.value = '';
      emit();
      input.focus();
    });
    // `/` from a focused row jumps to the field. Scoped to this container on
    // purpose — the GLOBAL `/` belongs to the command palette and must stay
    // untouched; this binding only fires when focus is already inside the
    // section list, where the palette shortcut does not reach.
    this._container.addEventListener('keydown', (e) => {
      if (e.key !== '/' || e.target === input) return;
      if (e.target.isContentEditable) return;
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      e.preventDefault();
      e.stopPropagation();
      input.focus();
      input.select();
    });
  }

  /** Full mode: tab shell + section list + theme/properties mount points. */
  _renderFullMode() {
    this._container.innerHTML = `
      <div class="pb-sl">
        <div class="pb-sl-tabs">
          <button class="pb-sl-tab pb-sl-tab-active" data-tab="sections">
            Sections <span class="pb-sl-tab-badge" id="pb-section-count">0</span>
          </button>
          <button class="pb-sl-tab" data-tab="theme">Theme</button>
          <button class="pb-sl-tab pb-sl-tab-hidden" data-tab="properties" id="pb-sl-tab-properties">Properties</button>
        </div>
        <div class="pb-sl-tab-content" id="pb-tab-sections">
          <div class="pb-sl-header">
            <span>Sections</span>
            <div class="pb-sl-header-actions">
              <span class="pb-sl-inline-loading" id="pb-sl-inline-loading" hidden>
                <span class="pb-sl-inline-loading-spinner" aria-hidden="true"></span>
                <span class="pb-sl-inline-loading-text">Loading\u2026</span>
              </span>
              <button class="pb-btn pb-btn-ghost pb-sl-seo-btn" data-testid="sl-seo-btn" data-tooltip="Page SEO settings">SEO</button>
              <button class="pb-btn pb-btn-ghost pb-sl-select-toggle" data-testid="sl-select-toggle" aria-pressed="false" data-tooltip="Select multiple sections">Select</button>
              <button class="pb-btn pb-btn-primary pb-sl-add-btn" data-testid="sl-add-section" data-tooltip="Add section">+ Add Section</button>
            </div>
          </div>
          <div class="pb-sl-clipboard-bar" data-testid="sl-clipboard-bar" hidden>
            <span class="pb-sl-clipboard-chip-slot" data-testid="sl-clipboard-slot"></span>
          </div>
          <div class="pb-sl-bulk-bar" data-testid="sl-bulk-bar" role="toolbar" aria-label="Selected sections" aria-controls="pb-sl-body-list" hidden></div>
          <div class="pb-sl-body" id="pb-sl-body-list" role="list" aria-label="Sections"></div>
        </div>
        <div class="pb-sl-tab-content" id="pb-tab-theme" style="display:none"></div>
        <div class="pb-sl-tab-content" id="pb-tab-properties" style="display:none"></div>
      </div>
    `;

    this._bodyEl = this._container.querySelector('.pb-sl-body');
    this._container
      .querySelector('[data-testid="sl-add-section"]')
      .addEventListener('click', () => this._openAddModal(null));

    // SEO button — opens Properties tab with SEO panel (no section selected)
    this._container.querySelector('[data-testid="sl-seo-btn"]').addEventListener('click', () => {
      this._selectedInstanceId = null;
      eventBus.emit('section-deselected');
      this._setPropertiesTabVisible(true);
      this._switchTab('properties');
    });

    // Tab switching
    this._activeTab = 'sections';
    for (const tab of this._container.querySelectorAll('.pb-sl-tab')) {
      tab.addEventListener('click', () => this._switchTab(tab.dataset.tab));
    }

    this._wireSelectionChrome();
    this._updateClipboardChip();
  }

  // ── Multi-select chrome (16A1) ──
  // Shared by both templates: the header Select toggle, the bulk bar (built
  // once, OUTSIDE `_bodyEl` so the innerHTML rebuild cannot destroy it — the
  // `_ensureReorderHint` precedent), and the body background click-to-clear.

  _wireSelectionChrome() {
    const toggle = this._container.querySelector('[data-testid="sl-select-toggle"]');
    toggle?.addEventListener('click', () => {
      // A persistent, discoverable entry point on every pointer type — D5
      // rejected long-press-to-reveal (collides with the 250ms reorder
      // long-press and the iOS callout), and modifier-clicks teach nothing.
      // Exiting does NOT clear the selection.
      this._selectingMode = !this._selectingMode;
      toggle.setAttribute('aria-pressed', String(this._selectingMode));
      toggle.classList.toggle('pb-sl-select-toggle-on', this._selectingMode);
      this._container
        .querySelector('.pb-sl')
        ?.classList.toggle('pb-sl-selecting', this._selectingMode);
    });

    // Click on the list background (the element itself, not a row) clears.
    this._bodyEl?.addEventListener('click', (e) => {
      if (e.target !== this._bodyEl) return;
      eventBus.emit('selection:request', { intent: 'clear', source: 'list-background' });
    });

    this._buildBulkBar();
  }

  _buildBulkBar() {
    const bar = this._container.querySelector('.pb-sl-bulk-bar');
    if (!bar) return;
    bar.innerHTML = '';

    // Deliberately NOT a live region — the #pb-sl-selection-live status region
    // announces the same state; two regions double-announce.
    const count = document.createElement('span');
    count.className = 'pb-sl-bulk-count';
    count.setAttribute('data-testid', 'sl-bulk-count');
    bar.appendChild(count);

    // Structural bulk actions (16A2b): every one is `onAsyncClick` +
    // `errorScope`, never `onClick` — that supplies the single-flight guard
    // (a second click mid-flight is swallowed), the pending state, the
    // guaranteed restore, and reportError(scope, err, {user:'toast'}) by
    // construction. Each action re-reads the live selection at click time.
    const duplicateBtn = createButton({
      variant: 'ghost',
      size: 'sm',
      label: 'Duplicate',
      ariaLabel: 'Duplicate selected sections',
      icon: ICON_DUPLICATE,
      labelClass: 'pb-sl-bulk-btn-label',
      testid: 'sl-bulk-duplicate',
      className: 'pb-sl-bulk-duplicate',
      errorScope: 'Bulk duplicate',
      onAsyncClick: async () => {
        const ids = selection.getIds();
        if (ids.length < 2) return;
        await sectionManager.bulkDuplicateSections(ids);
      }
    });
    const moveUpBtn = createButton({
      variant: 'ghost',
      size: 'sm',
      label: 'Move up',
      ariaLabel: 'Move selected sections up',
      icon: ICON_CHEVRON_UP,
      labelClass: 'pb-sl-bulk-btn-label',
      testid: 'sl-bulk-move-up',
      className: 'pb-sl-bulk-move-up',
      errorScope: 'Bulk move',
      onAsyncClick: async () => {
        const ids = selection.getIds();
        if (ids.length < 2) return;
        await sectionManager.bulkMoveSections(ids, { direction: 'up' });
      }
    });
    const moveDownBtn = createButton({
      variant: 'ghost',
      size: 'sm',
      label: 'Move down',
      ariaLabel: 'Move selected sections down',
      icon: ICON_CHEVRON_DOWN,
      labelClass: 'pb-sl-bulk-btn-label',
      testid: 'sl-bulk-move-down',
      className: 'pb-sl-bulk-move-down',
      errorScope: 'Bulk move',
      onAsyncClick: async () => {
        const ids = selection.getIds();
        if (ids.length < 2) return;
        await sectionManager.bulkMoveSections(ids, { direction: 'down' });
      }
    });
    const deleteBtn = createButton({
      variant: 'ghost',
      size: 'sm',
      label: 'Delete',
      ariaLabel: 'Delete selected sections',
      icon: ICON_TRASH,
      labelClass: 'pb-sl-bulk-btn-label',
      testid: 'sl-bulk-delete',
      className: 'pb-sl-bulk-delete',
      errorScope: 'Bulk delete',
      onAsyncClick: async () => {
        const ids = selection.getIds();
        if (ids.length < 2) return;
        await this._runBulkDelete(ids);
      }
    });
    const clearBtn = createButton({
      variant: 'ghost',
      size: 'sm',
      label: 'Clear',
      ariaLabel: 'Clear selection',
      labelClass: 'pb-sl-bulk-btn-label',
      testid: 'sl-bulk-clear',
      className: 'pb-sl-bulk-clear',
      onClick: () => {
        eventBus.emit('selection:request', { intent: 'clear', source: 'bulk-bar' });
      }
    });
    for (const btn of [duplicateBtn, moveUpBtn, moveDownBtn, deleteBtn, clearBtn]) {
      bar.appendChild(btn);
    }

    this._bulkBarEls = { bar, count, duplicateBtn, moveUpBtn, moveDownBtn, deleteBtn, clearBtn };
  }

  /** Ids of visible page rows (filtered + shared excluded), DOM order. */
  _visibleCandidateOrder() {
    if (!this._bodyEl) return [];
    const rows = this._bodyEl.querySelectorAll(
      '.pb-sl-row:not(.pb-sl-shared):not(.pb-sl-row-filtered)'
    );
    return [...rows].map((r) => r.dataset.instanceId).filter(Boolean);
  }

  /** Ids of ALL visible rows (shared included) — the roving-focus order. */
  _visibleFocusOrder() {
    if (!this._bodyEl) return [];
    const rows = this._bodyEl.querySelectorAll('.pb-sl-row:not(.pb-sl-row-filtered)');
    return [...rows].map((r) => r.dataset.instanceId).filter(Boolean);
  }

  _switchTab(tabName) {
    if (this._mode === 'sidebar') return; // NavSidebar owns tab switching
    if (this._activeTab === tabName) return;
    const previousTab = this._activeTab;
    this._activeTab = tabName;
    // Ensure Properties tab is visible when switching to it
    if (tabName === 'properties') this._setPropertiesTabVisible(true);
    for (const tab of this._container.querySelectorAll('.pb-sl-tab')) {
      tab.classList.toggle('pb-sl-tab-active', tab.dataset.tab === tabName);
    }

    const tabIds = {
      sections: '#pb-tab-sections',
      theme: '#pb-tab-theme',
      properties: '#pb-tab-properties'
    };
    const incomingEl = this._container.querySelector(tabIds[tabName]);
    const outgoingEl = previousTab ? this._container.querySelector(tabIds[previousTab]) : null;

    if (outgoingEl && outgoingEl !== incomingEl) {
      outgoingEl.classList.remove('pb-sl-tab-content-enter');
      outgoingEl.classList.add('pb-sl-tab-content-exit');
      setTimeout(() => {
        // Ignore stale timeout if user switched back before this fired.
        if (this._activeTab === previousTab) return;
        outgoingEl.style.display = 'none';
        outgoingEl.classList.remove('pb-sl-tab-content-exit');
      }, ANIM_MS);
    }

    if (incomingEl) {
      incomingEl.style.display = 'flex';
      incomingEl.classList.remove('pb-sl-tab-content-exit', 'pb-sl-tab-content-enter');
      // Force reflow so repeated toggles retrigger animation.
      void incomingEl.offsetWidth;
      incomingEl.classList.add('pb-sl-tab-content-enter');
      incomingEl.addEventListener(
        'animationend',
        () => {
          incomingEl.classList.remove('pb-sl-tab-content-enter');
        },
        { once: true }
      );
      requestAnimationFrame(() => this._focusFirstInTab(incomingEl));
    }

    // Notify PropertyPanel so it can update its internal _activeTab
    const ppTab = tabName === 'theme' ? 'theme' : tabName === 'properties' ? 'section' : null;
    if (ppTab) eventBus.emit('sidebar:tab-changed', { tab: ppTab });
  }

  switchTab(tabName) {
    this._switchTab(tabName);
  }

  _setPropertiesTabVisible(visible) {
    if (this._mode === 'sidebar') return; // NavSidebar owns visibility
    const tab = this._container.querySelector('#pb-sl-tab-properties');
    if (!tab) return;
    tab.classList.toggle('pb-sl-tab-hidden', !visible);
  }

  /** Return the theme tab container element for PropertyPanel to mount into. */
  get themeContainer() {
    if (this._mode === 'sidebar') return null; // NavSidebar owns mount points
    return this._container.querySelector('#pb-tab-theme');
  }

  /** Return the properties tab container element for PropertyPanel to mount into. */
  get propertiesContainer() {
    if (this._mode === 'sidebar') return null; // NavSidebar owns mount points
    return this._container.querySelector('#pb-tab-properties');
  }

  // _showLoadingSkeleton() was deleted here. `_render()` painted it and the
  // constructor called `_renderRows()` in the SAME tick, so it was overwritten
  // before a frame ever composited — it had never been visible. Reviving it was
  // the wrong fix: rows render from store state, which is already in memory, and
  // the 2.2MB section-index only supplies thumbnail/description/category while
  // the row's thumb box already reserves that space. There is no window in which
  // replacing real, interactive rows with placeholders is correct.

  _updateInlineLoading() {
    const statusEl = this._container.querySelector('#pb-sl-inline-loading');
    const textEl = this._container.querySelector('.pb-sl-inline-loading-text');
    if (!statusEl || !textEl) return;

    if (this._addModalLoading > 0) {
      textEl.textContent = 'Opening section picker…';
      statusEl.hidden = false;
      statusEl.classList.add('pb-sl-inline-loading-show');
      return;
    }

    if (this._pendingSectionOps > 0) {
      textEl.textContent = this._latestSectionOp === 'swap' ? 'Swapping\u2026' : 'Loading\u2026';
      statusEl.hidden = false;
      statusEl.classList.add('pb-sl-inline-loading-show');
      return;
    }

    statusEl.classList.remove('pb-sl-inline-loading-show');
    statusEl.hidden = true;
  }

  _setAddModalLoading(loading) {
    if (loading) this._addModalLoading++;
    else this._addModalLoading = Math.max(0, this._addModalLoading - 1);
    this._updateInlineLoading();
  }

  /**
   * The section-list used to render a clipboard chip ("Paste at end") in its
   * header + empty-state slots whenever something was on the clipboard. That
   * surface was retired in favor of the panel-bottom-zone tray, which shows
   * the same chip globally across all tabs (Sections, Design, Project,
   * Audit). Two copies on screen at once is confusing, and "paste a section
   * at the end of the page" is a global action that doesn't need to be
   * duplicated inside the Sections tab body.
   *
   * The slot containers (`.pb-sl-clipboard-bar` / `.pb-sl-clipboard-chip-slot`)
   * are still emitted by the row templates so this stub can keep them hidden
   * without a coordinated template change. The unused `getSectionClipboard` /
   * `clearSectionClipboard` imports were dropped at the top of the file.
   */
  /**
   * SS-4 — the clipboard is a TILE in the list (PP-0: anything a user can
   * click to insert renders in the list, never in a tray beside it). One
   * builder serves both homes: the end of the row list (_renderRows) and the
   * empty states' chip slots (this method). The bottom-zone SECTION tray is
   * retired; the element tray is untouched.
   *
   * @param {number|null} position — insert index; null = end of the page
   */
  _buildClipboardTile(position = null) {
    const section = getSectionClipboard();
    if (!section) return null;
    const ctx = this._resolveRowContext(section);
    const tile = document.createElement('button');
    tile.type = 'button';
    tile.className = 'pb-sl-clip-tile';
    tile.setAttribute('data-testid', 'sl-clipboard-tile');
    const name = ctx.displayName;
    tile.setAttribute('aria-label', `Paste “${name}” at the end of the page`);

    const thumbBox = document.createElement('span');
    thumbBox.className = 'pb-sl-thumb pb-sl-clip-tile-thumb';
    if (ctx.thumb) {
      const img = document.createElement('img');
      img.alt = '';
      applyThumbnail(img, ctx.thumb, { onExhausted: () => img.remove() });
      thumbBox.appendChild(img);
    } else {
      thumbBox.textContent = '⧉';
    }
    tile.appendChild(thumbBox);

    const text = document.createElement('span');
    text.className = 'pb-sl-clip-tile-text';
    const title = document.createElement('span');
    title.className = 'pb-sl-clip-tile-title';
    title.textContent = `Paste “${name}” here`;
    const sub = document.createElement('span');
    sub.className = 'pb-sl-clip-tile-sub';
    sub.textContent = 'On your clipboard · click to add at the end';
    text.append(title, sub);
    tile.appendChild(text);

    tile.addEventListener('click', () => {
      const state = store.getState();
      if (!state) return;
      const pageIndex = state.pages.findIndex((p) => p.id === state.activePage);
      if (pageIndex === -1) return;
      const page = state.pages[pageIndex];
      const at = Number.isInteger(position) ? position : (page.sections || []).length;
      pasteSectionAt(pageIndex, at); // paste ends with its own undo- toast
    });
    return tile;
  }

  _updateClipboardChip() {
    const slots = this._container?.querySelectorAll('.pb-sl-clipboard-chip-slot');
    if (!slots || slots.length === 0) return;
    for (const slot of slots) {
      slot.innerHTML = '';
      const tile = hasSectionClipboard() ? this._buildClipboardTile() : null;
      if (tile) {
        slot.appendChild(tile);
        slot.closest('.pb-sl-clipboard-bar')?.removeAttribute('hidden');
      } else {
        slot.closest('.pb-sl-clipboard-bar')?.setAttribute('hidden', '');
      }
    }
  }

  _focusFirstInTab(tabEl) {
    const focusable = tabEl.querySelector(
      'button:not([disabled]):not([aria-disabled="true"]), [href], input:not([disabled]):not([aria-disabled="true"]), select:not([disabled]):not([aria-disabled="true"]), textarea:not([disabled]):not([aria-disabled="true"]), [tabindex]:not([tabindex="-1"]):not([aria-disabled="true"])'
    );
    if (focusable && typeof focusable.focus === 'function') focusable.focus();
  }

  // ── Row rendering ──

  /**
   * OPEN audit findings per section instance — `{blocking, warning}` keyed by
   * `location.instanceId`. Derived fresh from the last report each time so a
   * changed `auditAccepts` (an accept IS project state) is always honoured.
   */
  _computeAuditByInstance() {
    const findings = this._auditReport?.findings;
    if (!Array.isArray(findings) || findings.length === 0) return new Map();
    let accepts;
    try {
      accepts = store.getState()?.auditAccepts || null;
    } catch {
      accepts = null;
    }
    const { open } = disposeFindings(findings, accepts);
    const map = new Map();
    for (const finding of open) {
      const id = finding?.location?.instanceId;
      if (!id) continue;
      const sev = finding.severity;
      if (sev !== 'blocking' && sev !== 'warning') continue;
      const cur = map.get(id) || { blocking: 0, warning: 0 };
      cur[sev] += 1;
      map.set(id, cur);
    }
    return map;
  }

  /** Compact, ordered serialization of the audit map for the render signature. */
  _auditSignature() {
    const map = this._computeAuditByInstance();
    if (map.size === 0) return '';
    return [...map.entries()]
      .map(([id, c]) => `${id}:${c.blocking}:${c.warning}`)
      .sort()
      .join('|');
  }

  _buildRowsRenderSignature(state = store.getState()) {
    if (!state) return '';
    const page = (state.pages || []).find((p) => p.id === state.activePage) || null;
    const summarizeSection = (section, sharedType = '') => {
      if (!section) return '';
      return [
        sharedType,
        section.instanceId || '',
        section.catalogKey || '',
        section.category || '',
        section.displayName || '',
        section.sectionId || '',
        section.templateSiteName || '',
        section.html || '',
        section.css || '',
        JSON.stringify(section.overrides || null)
      ].join('\u001f');
    };
    return [
      state.activePage || '',
      summarizeSection(state.sharedSections?.navigation, 'navigation'),
      ...(page?.sections || []).map((section) => summarizeSection(section)),
      summarizeSection(state.sharedSections?.footer, 'footer'),
      // SS-3: findings are not store state - without this key the row flag
      // would never repaint when the audit report changes.
      this._auditSignature()
    ].join('\u001e');
  }

  _renderRows() {
    this._bodyEl.removeAttribute('aria-busy');
    const state = store.getState();
    if (!state) return;

    const page = store.getActivePage();
    if (!page) return;

    const nav = state.sharedSections?.navigation;
    const footer = state.sharedSections?.footer;
    const sections = page.sections || [];
    this._rowsRenderSignature = this._buildRowsRenderSignature(state);
    this._rowChecks.clear(); // rebuilt below with the rows
    // SS-3: one derivation per render; _buildRowMeta reads this map.
    this._auditByInstance = this._computeAuditByInstance();

    // Lazy section-index load: the 2.2MB index only supplies row thumbnails /
    // descriptions, so an empty project never fetches it (keeps cold boot light).
    // The moment there IS content to render, kick off the memoized load — it
    // re-renders once the index lands, filling in thumbnails. `_load()` is
    // idempotent (guarded by `_loadPromise`), so calling it every render is cheap.
    if ((nav || footer || sections.length > 0) && this._indexMap.size === 0) {
      void this._load();
    }

    // Update section count badge
    const badge = this._container.querySelector('#pb-section-count');
    if (badge) badge.textContent = sections.length;

    this._bodyEl.innerHTML = '';

    // Nav row (pinned top) — wrapped in its shared group (headerless, SS-3).
    if (nav) {
      this._bodyEl.appendChild(this._buildSharedGroup(nav, 'navigation'));
    }

    // Page sections with insert markers
    if (sections.length === 0 && !nav && !footer) {
      // When the canvas empty stage is active, the giant doors on the canvas
      // already cover the "what should I do?" question. The sidebar shrinks
      // to a single quiet line so we don't double-shout the same message.
      if (document.body.classList.contains('pb-stage-active')) {
        this._bodyEl.innerHTML = `
          <div class="pb-empty-state-mini" data-testid="sl-empty-mini">
            <span class="pb-empty-state-mini-text">Sections you add will appear here.</span>
            <span class="pb-sl-clipboard-chip-slot" data-testid="sl-clipboard-slot-empty"></span>
          </div>
        `;
        this._updateClipboardChip();
        return;
      }
      // Quick-start chips: most-used categories. Each opens AddSectionModal
      // pre-filtered to that category. Icons are inline SVGs scoped via
      // .pb-empty-chip-icon so the global .pb-empty-state svg sizing rule
      // doesn't blow them up.
      const quickStart = [
        {
          id: 'hero',
          label: 'Hero',
          iconPath:
            '<rect x="3" y="4" width="18" height="11" rx="1.5"/><path d="M7 19h10M9 15v4M15 15v4"/>'
        },
        {
          id: 'services',
          label: 'Services',
          iconPath:
            '<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>'
        },
        {
          id: 'reviews',
          label: 'Reviews',
          iconPath:
            '<path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.62L12 2 9.19 8.62 2 9.24l5.46 4.73L5.82 21z"/>'
        },
        {
          id: 'pricing',
          label: 'Pricing',
          iconPath: '<path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>'
        },
        { id: 'cta', label: 'CTA', iconPath: '<path d="M5 12h14M13 6l6 6-6 6"/>' },
        {
          id: 'forms-contact',
          label: 'Contact',
          iconPath: '<path d="M4 4h16v16H4z"/><path d="m4 7 8 6 8-6"/>'
        }
      ];
      const chipsHTML = quickStart
        .map(
          (q) => `
        <button type="button" class="pb-empty-chip" data-category="${q.id}" data-testid="sl-empty-chip-${q.id}">
          <span class="pb-empty-chip-icon" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${q.iconPath}</svg>
          </span>
          <span>${q.label}</span>
        </button>
      `
        )
        .join('');

      this._bodyEl.innerHTML = `
        <div class="pb-empty-state pb-empty-state-hero">
          <div class="pb-empty-illustration" aria-hidden="true">
            <span class="pb-empty-block pb-empty-block-1"></span>
            <span class="pb-empty-block pb-empty-block-2"></span>
            <span class="pb-empty-block pb-empty-block-3"></span>
            <span class="pb-empty-glow"></span>
          </div>

          <div class="pb-empty-headline">Let's build your page</div>
          <div class="pb-empty-state-subtitle">Drop in a section to get started — or pick a template to skip ahead.</div>

          <div class="pb-empty-quick-start" role="group" aria-label="Quick-start sections">
            ${chipsHTML}
          </div>

          <span class="pb-sl-clipboard-chip-slot" data-testid="sl-clipboard-slot-empty"></span>

          <div class="pb-empty-ctas">
            <button class="pb-btn pb-btn-primary pb-sl-add-btn" data-testid="sl-add-section-empty" data-tooltip="Add section">+ Add Section</button>
            <button type="button" class="pb-btn pb-btn-ghost pb-empty-templates-btn" data-testid="sl-empty-templates">Browse templates</button>
          </div>
        </div>
      `;
      this._bodyEl
        .querySelector('[data-testid="sl-add-section-empty"]')
        ?.addEventListener('click', () => this._openAddModal(null));
      this._bodyEl
        .querySelector('[data-testid="sl-empty-templates"]')
        ?.addEventListener('click', () => eventBus.emit('toolbar:open-templates'));
      for (const chip of this._bodyEl.querySelectorAll('.pb-empty-chip')) {
        chip.addEventListener('click', () => {
          this._openAddModal(null, { category: chip.dataset.category });
        });
      }
      this._updateClipboardChip();
      return;
    }

    for (let i = 0; i < sections.length; i++) {
      this._bodyEl.appendChild(this._buildInsertMarker(i));
      this._bodyEl.appendChild(this._buildRow(sections[i], i, false, null));
    }

    // Final insert marker
    this._bodyEl.appendChild(this._buildInsertMarker(sections.length));

    // SS-4: the clipboard tile lives IN the list, before the footer (which
    // is spatially pinned last), pasting at the end of the page sections.
    if (hasSectionClipboard() && !this._searchQuery) {
      const tile = this._buildClipboardTile();
      if (tile) this._bodyEl.appendChild(tile);
    }

    // Footer row (pinned bottom) — wrapped in its shared group (headerless, SS-3).
    if (footer) {
      this._bodyEl.appendChild(this._buildSharedGroup(footer, 'footer'));
    }

    // Restore selection highlight. A live multi-selection repaints wholesale
    // from the store (the single-row `_highlightRow` would strip co-selected
    // rows on every 50ms-debounced rebuild); the scalar path is unchanged.
    if (!this._restoreSelectionPaint() && this._selectedInstanceId) {
      this._highlightRow(this._selectedInstanceId);
    }
    this._highlightActiveRow(this._activeVisibleInstanceId);
    this._syncRovingFocus();
    this._updateBulkBar();

    // Apply animation hints for recently changed rows
    const activePageId = state.activePage;
    const hints = consumeHints(activePageId);
    for (const hint of hints) {
      const row = this._bodyEl.querySelector(`[data-instance-id="${hint.instanceId}"]`);
      if (!row) continue;
      if (hint.type === 'added' || hint.type === 'duplicated') {
        // PP-2: a NEW row gets the "it landed here" ring, the one landed
        // keyframe every list and grid shares; a moved row keeps the slide.
        animateIn(row);
        landed(row);
      } else if (hint.type === 'moved') {
        animateIn(row);
      } else if (hint.type === 'swapped') {
        const thumb = row.querySelector('.pb-sl-thumb');
        if (thumb) animateIn(thumb);
      }
    }

    this._updateClipboardChip();

    // Re-apply any active search filter so rows render in their current
    // visibility state immediately after a re-render (state-changed,
    // clipboard-changed, etc.).
    this._applySearch();
  }

  _applySearch() {
    if (!this._bodyEl) return;
    const q = this._searchQuery || '';
    this._bodyEl.querySelector('.pb-sl-search-empty')?.remove();
    if (!q) {
      this._bodyEl.classList.remove('pb-sl-search-active');
      for (const row of this._bodyEl.querySelectorAll('.pb-sl-row')) {
        row.classList.remove('pb-sl-row-filtered');
      }
      return;
    }
    this._bodyEl.classList.add('pb-sl-search-active');
    let total = 0;
    let visible = 0;
    for (const row of this._bodyEl.querySelectorAll('.pb-sl-row')) {
      const haystack = row.dataset.searchText || '';
      const match = haystack.includes(q);
      row.classList.toggle('pb-sl-row-filtered', !match);
      total += 1;
      if (match) visible += 1;
    }
    // SS-4: an all-filtered list must say so, and the result count reaches
    // the screen reader through the SAME live region selection uses (one
    // region, one voice — a second region double-announces).
    if (visible === 0 && total > 0) {
      const empty = document.createElement('div');
      empty.className = 'pb-sl-search-empty';
      empty.setAttribute('data-testid', 'sl-search-empty');
      empty.textContent = `Nothing on this page matches “${this._searchQuery}”`;
      this._bodyEl.appendChild(empty);
    }
    this._announceSearch(
      `${visible} of ${total} section${total === 1 ? '' : 's'} match${visible === 1 ? 'es' : ''}`
    );
  }

  /** SS-4: search results speak through the SAME region selection uses (one
   * region, one voice), with the same clear-then-set + trailing debounce. */
  _announceSearch(text) {
    const region = this._ensureSelectionLiveRegion();
    clearTimeout(this._announceTimer);
    this._announceTimer = setTimeout(() => {
      region.textContent = '';
      requestAnimationFrame(() => {
        region.textContent = text;
      });
    }, 120);
  }

  // ── Build a section row ──

  _buildRow(section, index, isShared, sharedType) {
    const row = document.createElement('div');
    row.className = 'pb-sl-row';
    row.setAttribute('role', 'listitem');
    if (isShared) row.classList.add('pb-sl-shared');
    row.dataset.instanceId = section.instanceId;
    row.setAttribute('data-testid', `sl-row-${section.instanceId}`);

    const ctx = this._resolveRowContext(section);
    this._buildRowMain(row, section, ctx, isShared, sharedType);
    this._buildRowMeta(row, section, isShared);
    this._buildRowActions(row, section, index, isShared, sharedType);

    this._wireRowEvents(row, section.instanceId, { isShared });

    // Stamp searchable text so panel-top-zone's search filter can match
    // rows without a re-look-up. displayName covers the human-readable label;
    // catalogKey covers the stitch id / template origin (e.g.
    // "template-cliffsidecuts.com-hero-9012") so power-users can grep.
    // SS-4: the derived heading joins the bag, so search matches what the
    // row now SHOWS (its fact line), not only its internal identifiers.
    const searchBag = [
      ctx.displayName,
      ctx.facts?.heading,
      section.catalogKey,
      section.sectionId,
      section.templateSiteName
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    row.dataset.searchText = searchBag;

    return row;
  }

  // Phase 6 — wrap a shared section (nav or footer) in a labeled card group.
  // Shared variants use the same thumbnail picker as regular section rows.
  // SS-3 (D4): the repeated shared-group header is GONE — it used to
  // render twice in one list (nav at the top, footer at the bottom). The lock
  // glyph plus the row's "· shared" tag carry the meaning; the group element
  // itself survives because `.pb-sl-shared-group-footer` is the "insert at the
  // end" drop target (_resolveSectionDropTarget) and spatial order still
  // mirrors the page.
  _buildSharedGroup(section, sharedType) {
    const group = document.createElement('div');
    group.className = `pb-sl-shared-group pb-sl-shared-group-${sharedType}`;
    group.setAttribute('role', 'group');
    group.setAttribute(
      'aria-label',
      sharedType === 'navigation'
        ? 'Shared navigation, on every page'
        : 'Shared footer, on every page'
    );
    group.appendChild(this._buildRow(section, -1, true, sharedType));
    return group;
  }

  // Compute the per-row data both the main builder and the action popover need.
  _resolveRowContext(section) {
    const isFreeform = section.type === 'freeform';
    // SS-2: facts read from the section's own content feed the name ladder,
    // so an unclassified section is named by what it says instead of by the
    // parser's `unknown` marker. Memoized by the html string itself inside
    // section-facts (one parse per distinct body, whatever the render rate).
    const facts =
      !isFreeform && typeof section.html === 'string' && section.html
        ? sectionFactsFor(section.html, { parse: parseSectionHtml })
        : null;
    // Ladder shared with version-diff (the diff summaries must name sections
    // exactly the way this list does); `source` distinguishes a derived name
    // (evidence, rendered quoted in SS-3) from one a person typed.
    const resolved = resolveSectionName(section, this._categoryMap, {
      derivedName: facts?.heading || ''
    });
    const displayName = resolved.name;

    const thumbInfo = this._indexMap.get(section.catalogKey);
    let thumb = catalogSectionThumbnail(thumbInfo?.thumbnail, { sizes: ROW_THUMB_SIZES });
    if (
      section.templateSiteName &&
      section.templateSectionId &&
      shouldUseTemplateSectionThumbnail(section)
    ) {
      thumb = templateSectionThumbnail(section.templateSiteName, section.templateSectionId, {
        sizes: ROW_THUMB_SIZES
      });
    } else if (
      !section.templateSiteName &&
      !thumb &&
      section.catalogKey?.startsWith('template-') &&
      section.sectionId
    ) {
      const siteName = section.catalogKey.slice(
        'template-'.length,
        -(section.sectionId.length + 1)
      );
      thumb = templateSectionThumbnail(siteName, section.sectionId, { sizes: ROW_THUMB_SIZES });
    }
    // Two shapes on purpose. `thumb` is the descriptor the row's <img> needs
    // (srcset + fallback ladder); `thumbSrc` is the bare full-size URL, because
    // it is forwarded to the delete-confirm dialog as `confirmThumbnailSrc`,
    // which takes a plain string and has nowhere to put a ladder.
    // `nameSource`/`facts` ride along for the row renderer: SS-3 quotes a
    // derived name and builds the fact line from them.
    return {
      isFreeform,
      displayName,
      nameSource: resolved.source,
      facts,
      thumb,
      thumbSrc: thumb?.source || ''
    };
  }

  // Grip / lock + thumbnail + (label + stitch-id ghost subtitle).
  // One hidden hint shared by every row's aria-describedby — lives outside
  // _bodyEl so row re-renders don't destroy it.
  _ensureReorderHint() {
    if (this._container.querySelector('#pb-sl-reorder-hint')) return;
    const hint = document.createElement('span');
    hint.id = 'pb-sl-reorder-hint';
    hint.className = 'pb-sr-only';
    hint.textContent =
      'To reorder, drag the handle or press Alt+ArrowUp or Alt+ArrowDown while the row is focused. Press F2 to rename, Delete to remove. To select multiple sections, hold Shift and press ArrowUp or ArrowDown, or use the row checkboxes; press Escape to clear the selection.';
    this._container.appendChild(hint);
  }

  _buildRowMain(row, section, ctx, isShared, sharedType) {
    // Aliased: `thumb` is already the row's thumbnail BOX element below.
    const { isFreeform, displayName, thumb: thumbDescriptor } = ctx;

    if (!isShared) {
      const grip = document.createElement('span');
      grip.className = 'pb-sl-grip';
      grip.draggable = true;
      grip.setAttribute('role', 'img');
      grip.setAttribute('aria-label', 'Drag to reorder');
      grip.innerHTML = ICON_GRIP;
      this._wireDrag(grip, section.instanceId);
      row.appendChild(grip);
      // SR users can't operate the pointer-only grip — point them at the
      // keyboard path via a shared hidden hint (see _ensureReorderHint).
      this._ensureReorderHint();
      row.setAttribute('aria-describedby', 'pb-sl-reorder-hint');
    } else {
      const lock = document.createElement('span');
      lock.className = 'pb-sl-grip pb-sl-lock';
      lock.setAttribute('role', 'img');
      lock.setAttribute('aria-label', 'Shared across all pages');
      lock.innerHTML = ICON_LOCK;
      row.appendChild(lock);
    }

    // 16A1 — a real checkbox per page row: the only valid way to expose
    // selection state on role="listitem" (aria-selected/aria-checked are both
    // invalid there), and the touch/discoverability affordance. Shared rows get
    // none: they are scalar-only and can never join a multi-selection.
    if (!isShared) {
      const displayNameForCheck = ctx.displayName;
      const check = createCheckbox({
        ariaLabel: `Select ${displayNameForCheck}`,
        className: 'pb-sl-check',
        testid: `sl-check-${section.instanceId}`,
        checked: selection.isSelected(section.instanceId),
        onChange: () => {
          eventBus.emit('selection:request', {
            intent: 'toggle',
            id: section.instanceId,
            order: this._visibleCandidateOrder(),
            source: 'row-checkbox'
          });
        }
      });
      // The label wraps the input; without this stop the click also bubbles to
      // the row handler and immediately collapses the toggle it just made.
      check.wrap.addEventListener('click', (e) => e.stopPropagation());
      this._rowChecks.set(section.instanceId, check);
      row.appendChild(check.wrap);
    }

    const thumb = document.createElement('div');
    thumb.className = 'pb-sl-thumb';
    if (isFreeform) {
      thumb.classList.add('pb-sl-thumb-freeform');
      const placeholder = document.createElement('span');
      placeholder.className = 'pb-sl-freeform-icon';
      placeholder.textContent = '⬚';
      thumb.appendChild(placeholder);
    } else if (thumbDescriptor) {
      const img = document.createElement('img');
      img.alt = '';
      applyThumbnail(img, thumbDescriptor, {
        onExhausted: () => {
          const errIcon = document.createElement('span');
          errIcon.className = 'pb-sl-thumb-placeholder pb-sl-thumb-error';
          errIcon.setAttribute('data-tooltip', 'Preview unavailable');
          errIcon.setAttribute('role', 'img');
          errIcon.setAttribute('aria-label', 'Section preview failed to load');
          errIcon.innerHTML = ICON_THUMB_ERROR;
          img.replaceWith(errIcon);
        }
      });
      thumb.appendChild(img);
    }
    if (!isFreeform) {
      thumb.setAttribute('data-tooltip', 'Change variant');
      thumb.setAttribute('role', 'button');
      thumb.tabIndex = 0;
      thumb.setAttribute('aria-label', `Change variant for ${displayName}`);
      thumb.setAttribute('aria-expanded', 'false');
      // SS-5: the variant picker is on the collection kit, DYNAMICALLY
      // imported — a surface most sessions never open must not grow the
      // 271-file boot graph or its modulepreload list. It opens as a popover
      // BESIDE this row (the host it had before SS-5), never a dialog.
      const openVariantPicker = async (e) => {
        e.stopPropagation();
        // No early return on `aria-expanded`. It used to bail here whenever the
        // attribute was 'true', which is the whole time the picker is OPEN —
        // so the second press of the control that opened it did nothing at all.
        // In-flight de-duplication belongs to the picker session, which is the
        // only thing that can tell "still resolving" from "already open".
        this._collapseToRow(section.instanceId); // single-target rule (16A1)
        thumb.setAttribute('aria-expanded', 'true');
        thumb.classList.add('pb-sl-thumb-active');
        try {
          const { showVariantPicker } = await import('./variant-picker/session.js');
          await showVariantPicker({
            section,
            sharedType: isShared ? sharedType : null,
            categoryMap: this._categoryMap,
            anchor: { triggerEl: thumb, rowEl: row }
          });
        } catch (err) {
          reportError('SectionList', err, {
            user: 'toast',
            message: 'Could not open the variant picker.'
          });
        } finally {
          thumb.classList.remove('pb-sl-thumb-active');
          thumb.setAttribute('aria-expanded', 'false');
        }
      };
      thumb.addEventListener('click', openVariantPicker);
      thumb.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        openVariantPicker(e);
      });
    }
    row.appendChild(thumb);

    const textWrap = document.createElement('div');
    textWrap.className = 'pb-sl-text';

    const label = document.createElement('span');
    label.className = 'pb-sl-label';
    row.tabIndex = 0;
    row.setAttribute('aria-label', `Select section: ${displayName}`);
    if (isShared) {
      // SS-3 (D4): the group header is gone; the row itself says it is shared.
      const sharedName = sharedType === 'navigation' ? 'Navigation' : 'Footer';
      label.append(sharedName);
      const tag = document.createElement('em');
      tag.className = 'pb-sl-shared-tag';
      tag.textContent = '· shared';
      label.appendChild(tag);
      label.dataset.tooltip = `${sharedName} — shared across all pages`;
      row.setAttribute('aria-label', `Select section: ${sharedName}, shared across all pages`);
    } else if (ctx.nameSource === 'derived') {
      // A derived name is EVIDENCE (read from the section's own heading), not
      // a label someone chose — the quotes and lighter weight say so.
      label.classList.add('pb-sl-label-derived');
      label.textContent = `“${displayName}”`;
      label.dataset.tooltip = displayName;
    } else {
      label.textContent = displayName;
      label.dataset.tooltip = displayName;
    }

    if (!isShared) {
      label.setAttribute('role', 'button');
      label.tabIndex = 0;
      label.setAttribute('aria-label', `Rename section: ${displayName}`);
      label.addEventListener('dblclick', (e) => {
        e.stopPropagation();
        this._startRename(label, section.instanceId);
      });
      label.addEventListener('keydown', (e) => {
        if (label.contentEditable === 'true') return;
        if (e.key !== 'F2' && e.key !== 'Enter') return;
        e.preventDefault();
        e.stopPropagation();
        this._startRename(label, section.instanceId);
      });
    }
    textWrap.appendChild(label);

    // SS-3: the fact line carries what VARIES between rows — the recovered
    // heading when the name is a chosen/category one, then media counts. It
    // is RESERVED space (always visible, no hover growth), replacing the
    // hover-only `.pb-sl-stitch-id` ghost. The catalogKey is relocated, not
    // destroyed: it rides the row's title and the hover peek's footer.
    const facts = ctx.facts;
    const factBits = [];
    // The stitch handle leads the fact line, where the recovered heading used
    // to. SS-3 put the heading here and relocated the id to the row title and
    // the hover peek; the id is back in the open because it is the handle you
    // search, quote and match against the picker — and the heading was often
    // the same words as the name beside it, wrapped in quotes. Blank for a
    // template-derived section, which has no stitch identity.
    const handle = stitchHandle(section);
    if (!isShared && handle) factBits.push(handle);
    if (facts?.imageCount) {
      factBits.push(`${facts.imageCount} image${facts.imageCount === 1 ? '' : 's'}`);
    }
    if (facts?.linkCount) {
      factBits.push(`${facts.linkCount} link${facts.linkCount === 1 ? '' : 's'}`);
    }
    if (factBits.length) {
      const factsEl = document.createElement('span');
      factsEl.className = 'pb-sl-facts';
      factsEl.textContent = factBits.join(' · ');
      factsEl.dataset.tooltip = factsEl.textContent;
      textWrap.appendChild(factsEl);
    }
    if (section.catalogKey) {
      row.dataset.tooltip = `Stitch ID: ${section.catalogKey}`;
      row.dataset.catalogKey = section.catalogKey;
    }
    row.appendChild(textWrap);
  }

  // Image + SVG count badges (right-cluster, between text and the action trigger).
  _buildRowMeta(row, section, isShared) {
    // SS-3: the attention flag — OPEN blocking/warning findings for THIS
    // section, on the row where the section is. Always visible (never part of
    // the hover-revealed chrome): a fact about the row is not on-demand.
    const flagCounts = this._auditByInstance?.get(section.instanceId);
    if (flagCounts && (flagCounts.blocking || flagCounts.warning)) {
      const total = flagCounts.blocking + flagCounts.warning;
      const flag = document.createElement('span');
      flag.className = 'pb-sl-flag';
      if (flagCounts.blocking) flag.classList.add('pb-sl-flag-blocking');
      flag.setAttribute('role', 'button');
      flag.tabIndex = 0;
      flag.innerHTML = `${ICON_FLAG}<span>${total}</span>`;
      const tip = `${total} open finding${total > 1 ? 's' : ''} — open Publish checks`;
      flag.dataset.tooltip = tip;
      flag.setAttribute('aria-label', tip);
      flag.setAttribute('data-testid', `sl-flag-${section.instanceId}`);
      const open = (e) => {
        e.stopPropagation();
        eventBus.emit('props:request-tab', { tab: 'audit' });
      };
      flag.addEventListener('click', open);
      flag.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open(e);
        }
      });
      row.appendChild(flag);
    }

    const schedule = isShared ? '' : scheduleSummary(section.schedule);
    if (schedule) {
      const badge = document.createElement('span');
      badge.className = 'pb-sl-schedule-badge';
      badge.setAttribute('role', 'img');
      badge.innerHTML = ICON_CLOCK;
      badge.dataset.tooltip = `Scheduled: ${schedule}`;
      badge.setAttribute('aria-label', badge.title);
      badge.setAttribute('data-testid', `sl-schedule-${section.instanceId}`);
      row.appendChild(badge);
    }

    // Phase 7 — overrides badge. Renders only when this section carries any
    // per-element overrides; click jumps to the Design tab in section-view.
    const overrideCount = this._getOverridesCount(section);
    if (overrideCount > 0) {
      const badge = document.createElement('span');
      badge.className = 'pb-sl-overrides-badge';
      badge.setAttribute('role', 'button');
      badge.tabIndex = 0;
      badge.innerHTML = `${ICON_PALETTE}<span>${overrideCount}</span>`;
      const tip = `${overrideCount} override${overrideCount > 1 ? 's' : ''} — open in Design`;
      badge.dataset.tooltip = tip;
      badge.setAttribute('aria-label', tip);
      badge.setAttribute('data-testid', `sl-overrides-${section.instanceId}`);
      const open = (e) => {
        e.stopPropagation();
        // Select the section first, then ask the panel to switch to its
        // Design section-view (PropertyPanel listens to props:request-tab).
        // The legacy emit already collapses any multi-selection to this row.
        eventBus.emit('iframe:section-clicked', { instanceId: section.instanceId });
        eventBus.emit('props:request-tab', { tab: 'properties' });
      };
      badge.addEventListener('click', open);
      badge.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open(e);
        }
      });
      row.appendChild(badge);
    }

    const imgCount = this._getImageCount(section);
    if (imgCount > 0) {
      const imgBadge = document.createElement('span');
      imgBadge.className = 'pb-sl-img-badge';
      imgBadge.setAttribute('role', 'button');
      imgBadge.tabIndex = 0;
      imgBadge.innerHTML = `${ICON_IMAGE}<span>${imgCount}</span>`;
      imgBadge.dataset.tooltip = `${imgCount} image${imgCount > 1 ? 's' : ''}`;
      imgBadge.setAttribute('aria-label', imgBadge.title);
      imgBadge.setAttribute('aria-expanded', 'false');
      const toggleImg = (e) => {
        e.stopPropagation();
        this._toggleImagePanel(row, section);
      };
      imgBadge.addEventListener('click', toggleImg);
      imgBadge.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleImg(e);
        }
      });
      row.appendChild(imgBadge);
    }

    const svgCount = this._getSvgCount(section);
    if (svgCount > 0) {
      const badge = document.createElement('span');
      badge.className = 'pb-sl-svg-badge';
      badge.setAttribute('role', 'button');
      badge.tabIndex = 0;
      badge.innerHTML = `${ICON_IMAGE}<span>${svgCount}</span>`;
      badge.dataset.tooltip = `${svgCount} SVG asset${svgCount > 1 ? 's' : ''}`;
      badge.setAttribute('aria-label', badge.title);
      badge.setAttribute('aria-expanded', 'false');
      const toggleSvg = (e) => {
        e.stopPropagation();
        this._toggleSvgPanel(row, section);
      };
      badge.addEventListener('click', toggleSvg);
      badge.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleSvg(e);
        }
      });
      row.appendChild(badge);
    }
  }

  // Action trigger — opens the row actions popover. Replaces the previous
  // 5-inline-button cluster. Same handlers, same data-testids carry through
  // to the menu items so existing tests still find Duplicate/Copy/Delete.
  _buildRowActions(row, section, index, isShared, sharedType) {
    const actions = document.createElement('div');
    actions.className = 'pb-sl-actions';

    const trigger = createButton({
      variant: 'bare',
      className: 'pb-sl-actions-trigger',
      icon: ICON_DOTS_VERTICAL,
      tooltip: 'Section actions',
      ariaLabel: 'Section actions',
      ariaHaspopup: 'menu',
      ariaExpanded: 'false',
      testid: `sl-actions-${section.instanceId}`,
      onClick: (e) => {
        e.stopPropagation();
        this._toggleRowActionsMenu(trigger, actions, section, index, isShared, sharedType);
      }
    });
    actions.appendChild(trigger);
    row.appendChild(actions);
  }

  _toggleRowActionsMenu(trigger, actionsWrap, section, index, isShared, sharedType) {
    this._cancelThumbPreview();
    // Single-target rule (16A1): the ⋮ menu's actions are all single-target,
    // so opening it while multi-selected first collapses to this row. The
    // trigger is CSS-hidden at size > 1, but the command palette and tests can
    // still reach it — the collapse is the behavioural guarantee.
    this._collapseToRow(section.instanceId);
    if (this._activeRowActionsMenu && this._activeRowActionsMenu.trigger === trigger) {
      this._closeRowActionsMenu();
      return;
    }
    this._closeRowActionsMenu();

    const items = this._buildRowActionsItems(section, index, isShared, sharedType);
    const menu = document.createElement('div');
    menu.className = 'pb-sl-actions-menu';
    menu.setAttribute('role', 'menu');
    menu.setAttribute('aria-label', 'Section actions');
    menu.dataset.anchorInstanceId = section.instanceId;

    for (const item of items) {
      if (item.divider) {
        const sep = document.createElement('div');
        sep.className = 'pb-sl-actions-menu-sep';
        sep.setAttribute('role', 'separator');
        menu.appendChild(sep);
        continue;
      }
      const btn = createButton({
        variant: 'bare',
        className: item.danger
          ? 'pb-sl-actions-menu-item pb-sl-actions-menu-item-danger'
          : 'pb-sl-actions-menu-item',
        role: 'menuitem',
        disabled: !!item.disabled,
        tooltip: item.tooltip,
        testid: item.testid,
        icon: item.icon,
        iconClass: 'pb-sl-actions-menu-icon',
        label: item.label,
        labelClass: 'pb-sl-actions-menu-label',
        onClick: (e) => {
          e.stopPropagation();
          this._closeRowActionsMenu();
          item.onClick();
        }
      });
      menu.appendChild(btn);
    }

    actionsWrap.appendChild(menu);
    trigger.setAttribute('aria-expanded', 'true');

    const onDocClick = (e) => {
      if (menu.contains(e.target) || trigger.contains(e.target)) return;
      this._closeRowActionsMenu();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        this._closeRowActionsMenu({ restoreFocus: true });
      }
    };
    let listenersInstalled = false;
    const listenerFrame = requestAnimationFrame(() => {
      if (!this._activeRowActionsMenu || this._activeRowActionsMenu.menu !== menu) return;
      document.addEventListener('click', onDocClick);
      document.addEventListener('keydown', onKey);
      listenersInstalled = true;
    });

    this._activeRowActionsMenu = {
      menu,
      trigger,
      cleanup: () => {
        if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(listenerFrame);
        if (listenersInstalled) {
          document.removeEventListener('click', onDocClick);
          document.removeEventListener('keydown', onKey);
        }
      }
    };

    const firstItem = menu.querySelector('button:not([disabled])');
    firstItem?.focus();
  }

  _closeRowActionsMenu({ restoreFocus = false } = {}) {
    const active = this._activeRowActionsMenu;
    if (!active) return;
    this._activeRowActionsMenu = null;
    active.cleanup?.();
    active.trigger?.setAttribute('aria-expanded', 'false');
    if (active.menu?.isConnected) active.menu.remove();
    if (restoreFocus) active.trigger?.focus();
  }

  // Hover-to-preview thumb popover. After 250 ms of hover, a 240×135 preview
  // floats next to the row so users can confirm sections that share names.
  // Image is reused from the row's thumb (already loaded), so no fetch.
  _scheduleThumbPreview(row) {
    this._cancelThumbPreview();
    if (prefersReducedMotion()) return; // power users + a11y
    if (this._activeRowActionsMenu) return; // menu open — don't compete
    const img = row.querySelector('.pb-sl-thumb img');
    if (!img || !img.src) return; // freeform / no thumb
    this._thumbPreviewTimer = setTimeout(() => {
      this._thumbPreviewTimer = null;
      this._showThumbPreview(row, img.src);
    }, 250);
  }

  _cancelThumbPreview() {
    if (this._thumbPreviewTimer) {
      clearTimeout(this._thumbPreviewTimer);
      this._thumbPreviewTimer = null;
    }
    if (this._thumbPreviewEl) {
      this._thumbPreviewEl.hidden = true;
    }
  }

  _showThumbPreview(row, src) {
    let pop = this._thumbPreviewEl;
    if (!pop) {
      pop = document.createElement('div');
      pop.className = 'pb-sl-thumb-preview';
      pop.setAttribute('aria-hidden', 'true');
      const img = document.createElement('img');
      img.alt = '';
      pop.appendChild(img);
      const foot = document.createElement('span');
      foot.className = 'pb-sl-thumb-preview-id';
      pop.appendChild(foot);
      document.body.appendChild(pop);
      this._thumbPreviewEl = pop;
    }
    const img = pop.querySelector('img');
    if (img.src !== src) img.src = src;
    // SS-3: the catalogKey's new home — relocated off the always-visible
    // subtitle onto the peek, where the identity question is actually asked.
    const foot = pop.querySelector('.pb-sl-thumb-preview-id');
    const key = row.dataset.catalogKey || '';
    foot.textContent = key;
    foot.hidden = !key;

    // Position: prefer right of the row + 12 px gap, top-aligned to the row.
    // If the preview would overflow the viewport, flip to the left of the row.
    const rect = row.getBoundingClientRect();
    pop.hidden = false;
    const popRect = pop.getBoundingClientRect();
    const gap = 12;
    let left = rect.right + gap;
    if (left + popRect.width > window.innerWidth - 8) {
      left = Math.max(8, rect.left - popRect.width - gap);
    }
    let top = rect.top;
    if (top + popRect.height > window.innerHeight - 8) {
      top = Math.max(8, window.innerHeight - popRect.height - 8);
    }
    pop.style.left = `${left}px`;
    pop.style.top = `${top}px`;
  }

  _buildRowActionsItems(section, index, isShared, sharedType) {
    const items = [];

    if (!isShared) {
      const page = store.getActivePage();
      const sectionCount = page?.sections?.length || 0;

      items.push({
        icon: ICON_CHEVRON_UP,
        label: 'Move up',
        disabled: index === 0,
        tooltip: index === 0 ? 'Already first' : undefined,
        onClick: () => sectionManager.moveSection(section.instanceId, index - 1)
      });
      items.push({
        icon: ICON_CHEVRON_DOWN,
        label: 'Move down',
        disabled: index >= sectionCount - 1,
        tooltip: index >= sectionCount - 1 ? 'Already last' : undefined,
        onClick: () => sectionManager.moveSection(section.instanceId, index + 2)
      });
      items.push({ divider: true });
      items.push({
        icon: ICON_DUPLICATE,
        label: 'Duplicate',
        testid: `sl-duplicate-${section.instanceId}`,
        onClick: () => sectionManager.duplicateSection(section.instanceId)
      });
      items.push({
        icon: ICON_COPY,
        label: 'Copy section',
        testid: `sl-copy-${section.instanceId}`,
        onClick: () => copySectionById(section.instanceId)
      });
      items.push({
        icon: ICON_RENAME,
        label: 'Rename section',
        testid: `sl-rename-${section.instanceId}`,
        onClick: () => {
          const row = this._bodyEl?.querySelector(`[data-instance-id="${section.instanceId}"]`);
          const label = row?.querySelector('.pb-sl-label');
          if (label) this._startRename(label, section.instanceId);
        }
      });
      items.push({ divider: true });
    } else {
      // MM-3: swappable mobile drawer, navigation row only. Disabled with the
      // specific eligibility reason when the host nav doesn't match the
      // fragment contract (explain, not punish).
      if (sharedType === 'navigation') {
        const eligibility = checkNavHtmlEligibility(section.html);
        items.push({
          icon: ICON_PHONE,
          label: 'Mobile menu…',
          testid: `sl-mobile-menu-${section.instanceId}`,
          disabled: !eligibility.eligible,
          tooltip: eligibility.eligible ? undefined : eligibility.reasonCopy,
          onClick: () => {
            void this.openMobileMenuPicker();
          }
        });
        items.push({ divider: true });
      }
      // Shared sections (nav/footer) appear on every page — they can't be
      // duplicated or copied. Show the actions disabled-with-reason rather than
      // hiding them, so the affordance is discoverable (explain, not punish).
      items.push({
        icon: ICON_DUPLICATE,
        label: 'Duplicate',
        disabled: true,
        tooltip: 'Shared sections appear on every page and can’t be duplicated'
      });
      items.push({
        icon: ICON_COPY,
        label: 'Copy section',
        disabled: true,
        tooltip: 'Shared sections appear on every page and can’t be copied'
      });
      items.push({ divider: true });
    }

    // BC-8: act, then Undo — the Undo toast carries the way back, so the label
    // no longer has to.
    items.push({
      icon: ICON_TRASH,
      label: 'Delete',
      danger: true,
      testid: `sl-delete-${section.instanceId}`,
      onClick: () => sectionService.remove(section.instanceId, { announce: true })
    });

    return items;
  }

  _makeBtn(iconHtml, title, onClick) {
    const btn = document.createElement('button');
    btn.className = 'pb-sl-btn';
    btn.innerHTML = iconHtml;
    btn.setAttribute('data-tooltip', title);
    btn.setAttribute('aria-label', title);
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      onClick();
    });
    return btn;
  }

  // ── Insert marker ──

  _buildInsertMarker(position) {
    const marker = document.createElement('div');
    marker.className = 'pb-sl-insert';
    marker.dataset.position = String(position);
    marker.setAttribute('role', 'presentation');
    marker.setAttribute('data-testid', `sl-insert-${position}`);

    const btn = document.createElement('button');
    btn.className = 'pb-sl-insert-btn';
    btn.innerHTML = '+';
    btn.setAttribute('data-tooltip', 'Insert section here');
    btn.setAttribute('aria-label', 'Insert section here');
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      // The new section will land at `position`, so the upper neighbour is at
      // position-1. PP-1: the default tab is what the page most likely wants
      // NEXT (a hero is followed by services, not by a second hero) — the
      // shared sequence decides. The context sentence is derived in
      // _openAddModal for every caller.
      const state = store.getState();
      const page = state?.pages?.find?.((p) => p.id === state.activePage);
      const upperCategory = page?.sections?.[position - 1]?.category || null;
      const category = nextCategoryAfter(upperCategory, page?.sections || []);
      this._openAddModal(position, { category });
    });
    marker.appendChild(btn);

    if (hasSectionClipboard()) {
      const pasteBtn = document.createElement('button');
      pasteBtn.className = 'pb-sl-insert-paste';
      pasteBtn.type = 'button';
      pasteBtn.innerHTML = `${ICON_CLIPBOARD}<span>Paste</span>`;
      pasteBtn.setAttribute('data-tooltip', 'Paste copied section here');
      pasteBtn.setAttribute('aria-label', 'Paste copied section here');
      pasteBtn.setAttribute('data-testid', `sl-insert-paste-${position}`);
      pasteBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const state = store.getState();
        if (!state) return;
        const pageIndex = state.pages.findIndex((p) => p.id === state.activePage);
        if (pageIndex === -1) return;
        pasteSectionAt(pageIndex, position);
      });
      marker.appendChild(pasteBtn);
    }

    return marker;
  }

  // ── Row events ──

  _wireRowEvents(row, instanceId, opts = {}) {
    const { isShared = false } = opts;

    const selectRow = () => {
      // A plain click/Enter REPLACES the selection — including collapsing a
      // multi-selection to this row. The dedupe guard is keyed on the
      // mirrored primary, which `_syncSelectionUI` keeps equal to the multi
      // LEAD; without the isMulti test, clicking the lead row of {A,B,C}
      // repainted a single highlighted row while the store still held all
      // three, and the next Delete/Cmd+D acted on the hidden set.
      const alreadySelected = this._selectedInstanceId === instanceId && !selection.isMulti();
      this._selectedInstanceId = instanceId;
      this._setActiveVisibleInstance(instanceId);
      this._highlightRow(instanceId);
      bridge.send('scroll-to-section', { instanceId });
      // Only emit if selection actually changed — prevents redundant property panel updates
      if (!alreadySelected) {
        eventBus.emit('iframe:section-clicked', { instanceId });
      }
    };

    // Per EVENT, not per device (D6). A latched sidebar hover paints the canvas
    // — the highlight survives on a section the finger left long ago, because
    // a tap fires pointerenter and nothing fires pointerleave afterwards.
    //
    // Returning early also means the thumb preview never schedules on touch,
    // which is correct: it is a hover affordance with no touch equivalent, and
    // there is no gesture left to open it with that does not collide with the
    // 250ms reorder long-press.
    row.addEventListener('pointerenter', (e) => {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      if (!this._hoverCooldownActive) {
        eventBus.emit('section:hover', { instanceId });
      }
      this._scheduleThumbPreview(row);
    });

    row.addEventListener('pointerleave', (e) => {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      eventBus.emit('section:hover', { instanceId: null });
      this._cancelThumbPreview();
    });

    // Shift-click must extend the selection, not the browser's text selection.
    row.addEventListener('mousedown', (e) => {
      if (e.shiftKey) e.preventDefault();
    });

    row.addEventListener('click', (e) => {
      // Selecting mode (the touch path): a tap toggles and does NOT send
      // scroll-to-section — the user is picking a set, not navigating.
      if (this._selectingMode) {
        eventBus.emit('selection:request', {
          intent: 'toggle',
          id: instanceId,
          order: this._visibleCandidateOrder(),
          source: 'row-tap'
        });
        return;
      }
      const intent = resolveClickIntent(e);
      if (intent === 'replace') {
        // Plain click: today's path, byte-identical (16A0 parity gate).
        selectRow();
        return;
      }
      eventBus.emit('selection:request', {
        intent,
        id: instanceId,
        order: this._visibleCandidateOrder(),
        source: 'row-click'
      });
    });
    row.addEventListener('keydown', (e) => {
      if (e.target !== row) return; // children (label, thumb, etc.) handle their own keys
      this._handleRowKeydown(e, row, instanceId, { isShared, selectRow });
    });
  }

  // Phase 3 — keyboard shortcuts that fire only when the row itself is focused.
  // Browser defaults survive elsewhere because the guard above ensures we never
  // intercept keys typed into a child element (label rename, search input, etc.).
  _handleRowKeydown(e, row, instanceId, { isShared, selectRow }) {
    const key = e.key;
    const mod = e.metaKey || e.ctrlKey;
    const alt = e.altKey;
    const shift = e.shiftKey;

    // ── Multi-select keys (16A1) — BEFORE the existing branches so their
    // `!shift` guards keep working. ↑/↓ = roving focus; Shift+↑/↓ = extend the
    // range from the LEAD (focus follows it); Cmd+Space = toggle focused row.
    const selectionIntent = resolveRowKeyIntent(e);
    if (selectionIntent) {
      if (selectionIntent.type === 'focus-move') {
        e.preventDefault();
        const next = nextFocusTarget(instanceId, this._visibleFocusOrder(), selectionIntent.dir);
        if (next && next !== instanceId) this._focusRowById(next);
        return;
      }
      if (selectionIntent.type === 'extend') {
        e.preventDefault();
        if (isShared) return; // shared rows never join a range
        const order = this._visibleCandidateOrder();
        const target = extendTargetFromLead(
          selection.getLead(),
          order,
          selectionIntent.dir,
          instanceId
        );
        if (!target) return;
        eventBus.emit('selection:request', {
          intent: 'range',
          id: target,
          order,
          source: 'row-shift-arrow'
        });
        this._focusRowById(target);
        return;
      }
      if (selectionIntent.type === 'toggle-focused') {
        e.preventDefault();
        if (isShared) return; // scalar-only — a toggle would collapse anyway
        eventBus.emit('selection:request', {
          intent: 'toggle',
          id: instanceId,
          order: this._visibleCandidateOrder(),
          source: 'row-cmd-space'
        });
        return;
      }
    }

    // Enter / Space — select (existing behavior).
    if ((key === 'Enter' || key === ' ') && !mod && !alt && !shift) {
      e.preventDefault();
      selectRow();
      return;
    }

    // F2 — rename. Page sections only; shared rows have a fixed name.
    if (key === 'F2' && !mod && !alt && !shift && !isShared) {
      e.preventDefault();
      this._collapseToRow(instanceId); // single-target rule: collapse, then run unchanged
      const label = row.querySelector('.pb-sl-label');
      if (label) this._startRename(label, instanceId);
      return;
    }

    // Alt+↑ / Alt+↓ — reorder. Page sections only. A focused row INSIDE a
    // multi-selection moves the whole set as one compound entry (16A2b);
    // a row outside it collapses first (the single-target rule).
    if (alt && !mod && !shift && (key === 'ArrowUp' || key === 'ArrowDown') && !isShared) {
      e.preventDefault();
      const direction = key === 'ArrowUp' ? 'up' : 'down';
      if (selection.isMulti() && selection.isSelected(instanceId)) {
        sectionManager.bulkMoveSections(selection.getIds(), { direction });
        this._refocusRowAfterRender(instanceId);
        return;
      }
      this._collapseToRow(instanceId);
      this._keyboardMoveSection(row, instanceId, direction);
      return;
    }

    // Cmd/Ctrl+D — duplicate. Page sections only. Bulk at size > 1 when the
    // focused row is a member.
    if (mod && !alt && !shift && (key === 'd' || key === 'D') && !isShared) {
      e.preventDefault();
      e.stopPropagation(); // don't let global shortcut fire twice + suppress browser bookmark
      if (selection.isMulti() && selection.isSelected(instanceId)) {
        sectionManager.bulkDuplicateSections(selection.getIds());
        return;
      }
      this._collapseToRow(instanceId);
      sectionManager.duplicateSection(instanceId);
      return;
    }

    // Cmd/Ctrl+C — copy section. Page sections only (shared can't be re-pasted).
    if (mod && !alt && !shift && (key === 'c' || key === 'C') && !isShared) {
      e.preventDefault();
      e.stopPropagation();
      this._collapseToRow(instanceId);
      copySectionById(instanceId);
      return;
    }

    // Delete / Backspace — delete with confirm modal (sectionManager owns the
    // modal). Bulk at size > 1 when the focused row is a member: one confirm,
    // one entry, one undo.
    if (!mod && !alt && !shift && (key === 'Delete' || key === 'Backspace')) {
      e.preventDefault();
      e.stopPropagation();
      if (!isShared && selection.isMulti() && selection.isSelected(instanceId)) {
        this._runBulkDelete(selection.getIds());
        return;
      }
      this._collapseToRow(instanceId); // outside the selection → single-target
      this._keyboardDeleteSection(instanceId);
      return;
    }
  }

  _focusRowById(instanceId) {
    const row = this._bodyEl?.querySelector(
      `.pb-sl-row[data-instance-id="${CSS.escape(instanceId)}"]`
    );
    if (!row) return;
    // Roving: the focused row becomes the single tab stop.
    for (const r of this._bodyEl.querySelectorAll('.pb-sl-row')) r.tabIndex = -1;
    row.tabIndex = 0;
    row.focus({ preventScroll: false });
  }

  _keyboardMoveSection(row, instanceId, direction) {
    // Re-resolve index from current state — the index closure captured at
    // build time goes stale after any reorder, paste, or undo.
    const page = store.getActivePage();
    if (!page) return;
    const sections = page.sections || [];
    const idx = sections.findIndex((s) => s.instanceId === instanceId);
    if (idx === -1) return;
    const target = direction === 'up' ? idx - 1 : idx + 2; // moveSection uses pre-shift index
    if (direction === 'up' && idx === 0) return;
    if (direction === 'down' && idx >= sections.length - 1) return;
    sectionManager.moveSection(instanceId, target);
    // Restore focus to the row after the debounced re-render lands (~50ms).
    // The replacement row carries the same data-instance-id.
    this._refocusRowAfterRender(instanceId);
  }

  _refocusRowAfterRender(instanceId) {
    // state-changed → 50ms render debounce. Wait one render frame past that.
    setTimeout(() => {
      const next = this._bodyEl?.querySelector(`[data-instance-id="${instanceId}"]`);
      if (!next) return;
      next.focus?.({ preventScroll: true });
    }, 90);
  }

  _keyboardDeleteSection(instanceId) {
    // BC-8: the one policy — no dialog, an Undo toast, a refusal explained.
    void sectionService.remove(instanceId, { announce: true });
  }

  _highlightRow(instanceId) {
    const rows = this._bodyEl.querySelectorAll('.pb-sl-row');
    for (const r of rows) {
      r.classList.toggle('pb-sl-selected', r.dataset.instanceId === instanceId);
    }
    this._markAdjacentInserts();
    // Scroll the selected row into view in the list
    const selected = this._bodyEl.querySelector(`[data-instance-id="${instanceId}"]`);
    if (selected) selected.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  // ── Multi-select paint (16A1) ──

  /**
   * Paint the full selection state from a `selection:changed` payload. Never
   * re-renders the grid — classes, checkboxes and the bar are mutated in place
   * (the project-history-modal `syncSelectionUI` precedent).
   */
  _syncSelectionUI(change) {
    if (!this._bodyEl) return;
    const ids = new Set(change.ids);
    const multi = change.size > 1;

    // Keep the legacy mirror aligned: `primary` is what every single-target
    // consumer in this file reads.
    this._selectedInstanceId = change.primary;

    this._container.querySelector('.pb-sl')?.classList.toggle('pb-sl-multi', multi);

    for (const row of this._bodyEl.querySelectorAll('.pb-sl-row')) {
      const id = row.dataset.instanceId;
      const isSelected = ids.has(id);
      // `pb-sl-selected` on EVERY selected row — its six existing CSS
      // dependents (badge/actions/stitch-id reveal, popover-anchor exclusion,
      // hover) keep working for all members with zero churn.
      row.classList.toggle('pb-sl-selected', isSelected);
      row.classList.toggle('pb-sl-selected-lead', multi && id === change.lead);
      const check = this._rowChecks.get(id);
      if (check) check.setChecked(isSelected);
    }
    this._markAdjacentInserts();

    this._updateBulkBar(change);
    this._syncRovingFocus(change.lead);
    // Synthetic repaints (post-rebuild restore) stay silent — announcing
    // "N sections selected" again on every 50ms re-render is noise.
    if (!change.silent) this._announceSelection(change);
  }

  /** Re-apply selection paint after a full row rebuild (the 50ms debounce). */
  _restoreSelectionPaint() {
    if (selection.isMulti()) {
      this._syncSelectionUI({
        ids: selection.getIds(),
        size: selection.size(),
        primary: selection.getPrimary(),
        lead: selection.getLead(),
        silent: true
      });
      return true;
    }
    return false;
  }

  _updateBulkBar(change = null) {
    const els = this._bulkBarEls;
    if (!els) return;
    const ids = change ? change.ids : selection.getIds();
    const size = change ? change.size : ids.length;
    // No new chrome for the 95% case: hidden below size 2.
    if (size < 2) {
      els.bar.hidden = true;
      return;
    }
    const visible = new Set(this._visibleCandidateOrder());
    const summary = summarizeSelection(ids, visible);
    els.count.textContent = summary.text;
    els.bar.hidden = false;
  }

  /**
   * Roving row focus: exactly one row is tabbable; ↑/↓ move it. The tab stop
   * follows the lead so keyboard range extension continues from where the
   * selection is, falling back to the first visible row.
   */
  _syncRovingFocus(leadId = selection.getLead()) {
    if (!this._bodyEl) return;
    const rows = [...this._bodyEl.querySelectorAll('.pb-sl-row')];
    if (!rows.length) return;
    let target =
      (leadId && rows.find((r) => r.dataset.instanceId === leadId)) ||
      rows.find((r) => !r.classList.contains('pb-sl-row-filtered'));
    if (!target) target = rows[0];
    for (const row of rows) {
      row.tabIndex = row === target ? 0 : -1;
    }
  }

  /**
   * Live region for selection state — `role="status"`, appended to
   * `_container` (not `_bodyEl`, whose innerHTML rebuilds would destroy it).
   * Trailing-debounced at 120ms so a held Shift+↓ is one utterance, with the
   * clear-then-set-on-rAF idiom so repeat announcements re-fire.
   */
  _ensureSelectionLiveRegion() {
    let region = this._container.querySelector('#pb-sl-selection-live');
    if (!region) {
      region = document.createElement('div');
      region.id = 'pb-sl-selection-live';
      region.className = 'pb-sr-only';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('aria-atomic', 'true');
      this._container.appendChild(region);
    }
    return region;
  }

  _announceSelection(change) {
    // Single-select stays silent — it announced nothing before this phase, and
    // the selected row's aria-label already reads on focus.
    if (change.size <= 1 && (!change.previousIds || change.previousIds.length <= 1)) return;
    const region = this._ensureSelectionLiveRegion();
    clearTimeout(this._announceTimer);
    this._announceTimer = setTimeout(() => {
      const visible = new Set(this._visibleCandidateOrder());
      const { hiddenCount } = summarizeSelection(change.ids, visible);
      region.textContent = '';
      requestAnimationFrame(() => {
        region.textContent = selectionAnnouncement({ size: change.size, hiddenCount });
      });
    }, 120);
  }

  /**
   * Bulk delete with the list-owned parts: the hidden-by-filter disclosure
   * (named in the confirm before anything is destroyed) and the post-delete
   * focus chain. The chain deliberately never targets the Clear button — it
   * disappears the moment the selection falls below two.
   */
  async _runBulkDelete(ids) {
    const selectedSet = new Set(ids);
    const hiddenNames = [];
    let smallestDeletedIndex = Infinity;
    // Indexed over ALL rows (shared included) — _focusAfterBulkDelete
    // re-queries the same `.pb-sl-row` base after the rebuild, so the two
    // index spaces must match or a leading nav row shifts focus by one.
    const rows = [...(this._bodyEl?.querySelectorAll('.pb-sl-row') || [])];
    rows.forEach((row, index) => {
      if (!selectedSet.has(row.dataset.instanceId)) return;
      if (index < smallestDeletedIndex) smallestDeletedIndex = index;
      if (row.classList.contains('pb-sl-row-filtered')) {
        hiddenNames.push(row.querySelector('.pb-sl-label')?.textContent?.trim() || 'Section');
      }
    });
    const removed = await sectionService.bulkRemove({
      instanceIds: ids,
      hiddenNames,
      announce: true
    });
    if (!removed) return;
    eventBus.emit('section-deselected');
    this._focusAfterBulkDelete(smallestDeletedIndex);
  }

  /**
   * Focus chain after a bulk delete (runs after the debounced rebuild):
   * the row now at the smallest deleted index (clamped) → the last row →
   * the Select toggle → `_bodyEl` itself with tabindex="-1".
   */
  _focusAfterBulkDelete(smallestDeletedIndex) {
    setTimeout(() => {
      const rows = [...(this._bodyEl?.querySelectorAll('.pb-sl-row') || [])];
      const clamped = Math.min(
        Number.isFinite(smallestDeletedIndex) ? smallestDeletedIndex : 0,
        rows.length - 1
      );
      const target =
        rows[clamped] ||
        rows[rows.length - 1] ||
        this._container.querySelector('[data-testid="sl-select-toggle"]');
      if (target) {
        if (target.classList?.contains('pb-sl-row')) {
          for (const r of rows) r.tabIndex = -1;
          target.tabIndex = 0;
        }
        target.focus?.({ preventScroll: true });
        return;
      }
      if (this._bodyEl) {
        this._bodyEl.tabIndex = -1;
        this._bodyEl.focus?.({ preventScroll: true });
      }
    }, 90);
  }

  /**
   * The single-target collapse rule: while size > 1, any single-target action
   * reached anyway (row shortcut, menu, command palette) first collapses the
   * selection to the owning row, then runs unchanged.
   */
  _collapseToRow(instanceId) {
    if (!selection.isMulti()) return;
    eventBus.emit('selection:request', {
      intent: 'replace',
      id: instanceId,
      source: 'single-target-collapse'
    });
  }

  /**
   * Reveal the two insert markers flanking the selected row.
   *
   * The markers otherwise reveal on `.pb-sl-row:hover + .pb-sl-insert` only, so
   * on touch they are unreachable: a finger has no hover, and at rest a marker
   * is 6px tall at zero opacity. Selection is the touch equivalent of hover
   * (D5), so it reveals them the way a mouse hover does.
   *
   * Unlike the glass overlay's add buttons, DOM adjacency is meaningful here —
   * `_renderBody` appends marker(0), row(0), marker(1), row(1) … in order — so
   * the flanking markers are literally the previous and next siblings. The
   * attribute still beats a `+` selector because it survives on touch, where
   * `:hover` both latches and never arrives.
   */
  _markAdjacentInserts() {
    if (!this._bodyEl) return;
    for (const m of this._bodyEl.querySelectorAll('.pb-sl-insert[data-adjacent]')) {
      m.removeAttribute('data-adjacent');
    }
    const selected = this._bodyEl.querySelector('.pb-sl-row.pb-sl-selected');
    if (!selected) return;
    for (const sib of [selected.previousElementSibling, selected.nextElementSibling]) {
      if (sib?.classList?.contains('pb-sl-insert')) sib.setAttribute('data-adjacent', '');
    }
  }

  _highlightActiveRow(instanceId) {
    const rows = this._bodyEl.querySelectorAll('.pb-sl-row');
    for (const r of rows) {
      r.classList.toggle('pb-sl-active', !!instanceId && r.dataset.instanceId === instanceId);
    }
  }

  _setActiveVisibleInstance(instanceId) {
    const next = instanceId || null;
    if (this._activeVisibleInstanceId === next) return;
    this._activeVisibleInstanceId = next;
    this._highlightActiveRow(next);
  }

  _getPreviewViewportHeight() {
    const iframe = document.getElementById('pb-preview');
    if (iframe?.clientHeight) return iframe.clientHeight;
    const wrap = iframe?.closest('.pb-iframe-wrap');
    if (wrap?.clientHeight) return wrap.clientHeight;
    return window.innerHeight || 0;
  }

  _pickMostVisibleSection(positions, scrollY, viewportHeight) {
    if (!positions.length) return null;
    if (!viewportHeight) return positions[0] || null;

    const viewportTop = scrollY;
    const viewportBottom = scrollY + viewportHeight;

    let best = null;
    let bestOverlap = -1;
    for (const pos of positions) {
      const top = Number(pos?.top) || 0;
      const height = Number(pos?.height) || 0;
      const bottom = top + height;
      const overlap = Math.max(0, Math.min(bottom, viewportBottom) - Math.max(top, viewportTop));
      if (overlap > bestOverlap) {
        best = pos;
        bestOverlap = overlap;
      }
    }
    if (bestOverlap > 0) return best;

    const anchorY = viewportTop + viewportHeight * 0.33;
    let nearest = positions[0];
    let minDist = Infinity;
    for (const pos of positions) {
      const top = Number(pos?.top) || 0;
      const bottom = top + (Number(pos?.height) || 0);
      let dist = 0;
      if (anchorY < top) dist = top - anchorY;
      else if (anchorY > bottom) dist = anchorY - bottom;
      if (dist < minDist) {
        minDist = dist;
        nearest = pos;
      }
    }
    return nearest;
  }

  _syncActiveVisibleSection(data) {
    if (!data || !Array.isArray(data.positions) || data.positions.length === 0) return;
    const pagePositions = data.positions.filter((p) => !p?.shared);
    if (pagePositions.length === 0) return;
    const scrollY = Number(data.scrollY) || 0;
    const viewportHeight = this._getPreviewViewportHeight();
    const active = this._pickMostVisibleSection(pagePositions, scrollY, viewportHeight);
    this._setActiveVisibleInstance(active?.instanceId || null);
  }

  // ── Drag and drop ──

  _clearSectionDropIndicators() {
    if (!this._bodyEl) return;
    for (const row of this._bodyEl.querySelectorAll('.pb-sl-row')) {
      row.classList.remove('pb-sl-drop-above', 'pb-sl-drop-below');
    }
    for (const marker of this._bodyEl.querySelectorAll('.pb-sl-insert')) {
      marker.classList.remove('pb-sl-drop-target');
    }
  }

  _resolveSectionDropTarget(rawTarget, clientY) {
    const page = store.getActivePage();
    if (!page || !Array.isArray(page.sections) || page.sections.length === 0) return null;
    const target = rawTarget?.nodeType === 1 ? rawTarget : rawTarget?.parentElement;
    const marker = target?.closest?.('.pb-sl-insert');
    if (marker && this._bodyEl.contains(marker)) {
      const position = Number(marker.dataset.position);
      if (Number.isInteger(position) && position >= 0 && position <= page.sections.length) {
        return { targetIndex: position, marker };
      }
    }

    const row = target?.closest?.('.pb-sl-row');
    if (row && this._bodyEl.contains(row)) {
      if (row.classList.contains('pb-sl-shared')) {
        const group = row.closest('.pb-sl-shared-group');
        return {
          targetIndex: group?.classList.contains('pb-sl-shared-group-footer')
            ? page.sections.length
            : 0,
          marker: null
        };
      }
      const targetIndex = page.sections.findIndex(
        (section) => section.instanceId === row.dataset.instanceId
      );
      if (targetIndex !== -1) {
        const rect = row.getBoundingClientRect();
        return {
          targetIndex: targetIndex + (clientY >= rect.top + rect.height / 2 ? 1 : 0),
          marker: null
        };
      }
    }

    // Insert markers intentionally have pointer-events:none except for their
    // buttons. For a bare gap/body target, resolve the nearest marker by Y.
    let nearestMarker = null;
    let nearestDistance = Infinity;
    for (const candidate of this._bodyEl.querySelectorAll('.pb-sl-insert')) {
      const rect = candidate.getBoundingClientRect();
      const distance = Math.abs(clientY - (rect.top + rect.height / 2));
      if (distance < nearestDistance) {
        nearestMarker = candidate;
        nearestDistance = distance;
      }
    }
    if (nearestMarker) {
      const position = Number(nearestMarker.dataset.position);
      if (Number.isInteger(position)) return { targetIndex: position, marker: nearestMarker };
    }
    return null;
  }

  _showSectionDropTarget(dropTarget) {
    this._clearSectionDropIndicators();
    if (!dropTarget) return;
    const page = store.getActivePage();
    if (!page) return;
    if (dropTarget.marker) dropTarget.marker.classList.add('pb-sl-drop-target');
    const targetIndex = dropTarget.targetIndex;
    const next = page.sections[targetIndex];
    const prev = page.sections[targetIndex - 1];
    const nextRow = next
      ? this._bodyEl.querySelector(`.pb-sl-row[data-instance-id="${CSS.escape(next.instanceId)}"]`)
      : null;
    const prevRow = prev
      ? this._bodyEl.querySelector(`.pb-sl-row[data-instance-id="${CSS.escape(prev.instanceId)}"]`)
      : null;
    if (nextRow) nextRow.classList.add('pb-sl-drop-above');
    else if (prevRow) prevRow.classList.add('pb-sl-drop-below');
  }

  _initDragDelegation() {
    if (this._dragDelegated) return;
    this._dragDelegated = true;

    this._bodyEl.addEventListener('dragover', (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      this._showSectionDropTarget(this._resolveSectionDropTarget(e.target, e.clientY));
    });

    this._bodyEl.addEventListener('drop', (e) => {
      e.preventDefault();
      const draggedId = e.dataTransfer.getData('text/plain');
      const dropTarget = this._resolveSectionDropTarget(e.target, e.clientY);
      if (!draggedId || !dropTarget) {
        this._cleanupDrag();
        return;
      }
      this._commitSectionDrop(draggedId, dropTarget.targetIndex);
      this._cleanupDrag();
    });

    // Window-level safety net for all drag exit paths (Escape, out-of-window release)
    // Remove any previous listener to prevent accumulation on re-init
    if (this._windowDragEnd) {
      window.removeEventListener('dragend', this._windowDragEnd, { capture: true });
    }
    this._windowDragEnd = () => this._cleanupDrag();
    window.addEventListener('dragend', this._windowDragEnd, { capture: true });
  }

  /**
   * One commit path for both drag transports (16A2b). A dragged row inside a
   * multi-selection moves the WHOLE selection as one compound entry; the
   * no-op check is by final permutation inside bulkMoveSections, never by
   * drop target (dropping a non-contiguous selection "onto itself" can still
   * reorder). A dragged row outside the selection collapsed at dragstart, so
   * it arrives here as a plain single move.
   */
  _commitSectionDrop(draggedId, targetIndex) {
    if (selection.isMulti() && selection.isSelected(draggedId)) {
      return sectionManager.bulkMoveSections(selection.getIds(), { targetIndex });
    }
    return sectionManager.moveSection(draggedId, targetIndex);
  }

  _wireDrag(gripEl, instanceId) {
    this._initDragDelegation();

    gripEl.addEventListener('dragstart', (e) => {
      e.stopPropagation();
      e.dataTransfer.setData('text/plain', instanceId);
      e.dataTransfer.effectAllowed = 'move';

      // Dragging a row that is NOT part of the multi-selection collapses to
      // it — acting on one row while five are painted selected is the exact
      // ambiguity the single-target rule exists to prevent.
      if (selection.isMulti() && !selection.isSelected(instanceId)) {
        this._collapseToRow(instanceId);
      }

      const row = gripEl.closest('.pb-sl-row');
      if (!row) return;

      // Custom drag ghost showing thumbnail + label
      const ghost = this._buildDragGhost(row);
      document.body.appendChild(ghost);
      e.dataTransfer.setDragImage(ghost, ghost.offsetWidth / 2, ghost.offsetHeight / 2);

      requestAnimationFrame(() => {
        row.classList.add('pb-sl-dragging');
        ghost.remove();
      });

      // Mark drag surface as active (scoped to section list, not global)
      this._bodyEl.classList.add('pb-drag-active');
    });

    gripEl.addEventListener('dragend', () => {
      this._cleanupDrag();
    });

    gripEl.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'touch' && e.pointerType !== 'pen') return;
      this._startSectionPointerDrag(e, gripEl, instanceId);
    });
    gripEl.addEventListener(
      'click',
      (e) => {
        if (Date.now() - (this._lastSectionPointerDrag || 0) < 500) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );
  }

  _startSectionPointerDrag(e, gripEl, instanceId) {
    if (this._sectionPointerDrag || e.button !== 0) return;
    const row = gripEl.closest('.pb-sl-row');
    if (!row) return;
    const state = {
      pointerId: e.pointerId,
      pointerType: e.pointerType,
      startX: e.clientX,
      startY: e.clientY,
      gripEl,
      row,
      instanceId,
      armed: false,
      timer: 0,
      ghost: null,
      removeListeners: null,
      // Edge autoscroll. The list has none today, so dragging toward the top of
      // a long list simply cannot reach the rows above the fold.
      lastClientX: e.clientX,
      lastClientY: e.clientY,
      autoScrollRaf: 0
    };
    this._sectionPointerDrag = state;

    const arm = () => {
      if (this._sectionPointerDrag !== state) return;
      // Same rule as the HTML5 dragstart: an unselected row leaving a
      // multi-selection behind collapses to itself before the drag begins.
      if (selection.isMulti() && !selection.isSelected(instanceId)) {
        this._collapseToRow(instanceId);
      }
      state.armed = true;
      state.timer = 0;
      gripEl.style.touchAction = 'none';
      gripEl.draggable = false;
      try {
        gripEl.setPointerCapture(state.pointerId);
      } catch {
        /* capture is best-effort */
      }
      row.classList.add('pb-sl-dragging');
      this._bodyEl.classList.add('pb-drag-active');
      state.ghost = this._buildDragGhost(row);
      document.body.appendChild(state.ghost);
      state.ghost.style.left = `${state.startX + 14}px`;
      state.ghost.style.top = `${state.startY + 14}px`;
    };

    const onMove = (moveEvent) => {
      if (this._sectionPointerDrag !== state || moveEvent.pointerId !== state.pointerId) return;
      if (!state.armed) {
        const dx = moveEvent.clientX - state.startX;
        const dy = moveEvent.clientY - state.startY;
        if (dx * dx + dy * dy > 100) this._cleanupDrag();
        return;
      }
      moveEvent.preventDefault();
      moveEvent.stopPropagation();
      if (state.ghost) {
        state.ghost.style.left = `${moveEvent.clientX + 14}px`;
        state.ghost.style.top = `${moveEvent.clientY + 14}px`;
      }
      state.lastClientX = moveEvent.clientX;
      state.lastClientY = moveEvent.clientY;
      this._updateDropTargetAt(moveEvent.clientX, moveEvent.clientY, moveEvent.target);
      this._pumpDragAutoScroll(state);
    };

    const finish = (upEvent) => {
      if (this._sectionPointerDrag !== state || upEvent.pointerId !== state.pointerId) return;
      const wasArmed = state.armed;
      let dropTarget = null;
      if (wasArmed) {
        upEvent.preventDefault();
        upEvent.stopPropagation();
        const underPointer =
          document.elementFromPoint?.(upEvent.clientX, upEvent.clientY) || upEvent.target;
        dropTarget = this._resolveSectionDropTarget(underPointer, upEvent.clientY);
        this._lastSectionPointerDrag = Date.now();
      }
      this._cleanupDrag();
      if (wasArmed && dropTarget) {
        void this._commitSectionDrop(instanceId, dropTarget.targetIndex);
      }
    };

    const cancel = (cancelEvent) => {
      if (this._sectionPointerDrag !== state || cancelEvent.pointerId !== state.pointerId) return;
      this._cleanupDrag();
    };
    document.addEventListener('pointermove', onMove, { capture: true, passive: false });
    document.addEventListener('pointerup', finish, true);
    document.addEventListener('pointercancel', cancel, true);
    state.removeListeners = () => {
      document.removeEventListener('pointermove', onMove, { capture: true });
      document.removeEventListener('pointerup', finish, true);
      document.removeEventListener('pointercancel', cancel, true);
    };

    if (e.pointerType === 'touch') state.timer = window.setTimeout(arm, 250);
    else {
      e.preventDefault();
      arm();
    }
  }

  _updateDropTargetAt(clientX, clientY, fallbackTarget) {
    const underPointer = document.elementFromPoint?.(clientX, clientY) || fallbackTarget;
    this._showSectionDropTarget(this._resolveSectionDropTarget(underPointer, clientY));
  }

  /**
   * Scroll the list while a touch drag rests near its edge.
   *
   * A SELF-PERPETUATING rAF loop, not a per-move handler: the whole point is
   * that it keeps scrolling while the finger is STATIONARY, and a finger held
   * still emits no pointermove. It re-arms itself while the delta is non-zero
   * and stops the frame it reaches an end or leaves the edge band.
   *
   * The drop target is recomputed every frame from the finger's last known
   * position, because the rows move under it — without that the list scrolls
   * while the indicator stays pinned to whatever was there when the finger
   * stopped.
   *
   * `computeAutoScrollDelta` is shared with reorder-preview's
   * `_autoScrollReorder` so the two drags cannot drift apart. Scale is 1:
   * unlike the iframe, the sidebar has no `zoom` applied.
   */
  _pumpDragAutoScroll(state) {
    if (state.autoScrollRaf || !this._bodyEl) return;
    const step = () => {
      state.autoScrollRaf = 0;
      if (this._sectionPointerDrag !== state || !state.armed) return;
      const body = this._bodyEl;
      const rect = body.getBoundingClientRect();
      const delta = computeAutoScrollDelta(state.lastClientY, rect.top, rect.bottom, 1);
      if (!delta) return;

      const before = body.scrollTop;
      body.scrollTop = before + delta;
      // Stop at the ends rather than spinning a loop that moves nothing.
      if (Math.abs(body.scrollTop - before) < 0.5) return;

      this._updateDropTargetAt(state.lastClientX, state.lastClientY);
      state.autoScrollRaf = requestAnimationFrame(step);
    };
    state.autoScrollRaf = requestAnimationFrame(step);
  }

  /** Single source of truth for all drag exit paths. */
  _cleanupDrag() {
    const pointerDrag = this._sectionPointerDrag;
    if (pointerDrag) {
      if (pointerDrag.timer) clearTimeout(pointerDrag.timer);
      if (pointerDrag.autoScrollRaf) cancelAnimationFrame(pointerDrag.autoScrollRaf);
      pointerDrag.removeListeners?.();
      if (pointerDrag.ghost?.isConnected) pointerDrag.ghost.remove();
      pointerDrag.gripEl.style.touchAction = '';
      pointerDrag.gripEl.draggable = true;
      try {
        if (pointerDrag.gripEl.hasPointerCapture?.(pointerDrag.pointerId)) {
          pointerDrag.gripEl.releasePointerCapture(pointerDrag.pointerId);
        }
      } catch {
        /* capture may already be gone */
      }
      this._sectionPointerDrag = null;
    }
    this._bodyEl.classList.remove('pb-drag-active');
    for (const r of this._bodyEl.querySelectorAll('.pb-sl-row')) {
      r.classList.remove('pb-sl-dragging', 'pb-sl-drop-above', 'pb-sl-drop-below');
    }
    this._clearSectionDropIndicators();
  }

  /** Build an offscreen ghost element for setDragImage — shows thumbnail + label. */
  _buildDragGhost(row) {
    const ghost = document.createElement('div');
    ghost.className = 'pb-drag-ghost';

    const thumb = row.querySelector('.pb-sl-thumb img');
    if (thumb) {
      const img = document.createElement('img');
      img.src = thumb.src;
      img.className = 'pb-drag-ghost-thumb';
      ghost.appendChild(img);
    }

    const label = row.querySelector('.pb-sl-label');
    const span = document.createElement('span');
    // A multi-selection drags as a block, and the ghost says so (16A2b).
    const multiDrag = selection.isMulti() && selection.isSelected(row.dataset.instanceId);
    span.textContent = multiDrag ? `${selection.size()} sections` : label?.textContent || 'Section';
    ghost.appendChild(span);

    ghost.style.position = 'fixed';
    ghost.style.top = '-200px';
    ghost.style.left = '-200px';

    return ghost;
  }

  // ── Rename ──

  _startRename(labelEl, instanceId) {
    this._collapseToRow(instanceId); // single-target rule (16A1) — covers dblclick + label F2 too
    labelEl.contentEditable = 'true';
    labelEl.focus();

    // Select all text
    const range = document.createRange();
    range.selectNodeContents(labelEl);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);

    const finish = () => {
      labelEl.contentEditable = 'false';
      const newName = labelEl.textContent.trim();
      if (newName) {
        // Find the section in state and update displayName
        const state = store.getState();
        const page = store.getActivePage();
        if (!page) return;

        const pageIndex = state.pages.findIndex((p) => p.id === page.id);
        const secIndex = page.sections.findIndex((s) => s.instanceId === instanceId);
        if (pageIndex !== -1 && secIndex !== -1) {
          store.update(
            `pages.${pageIndex}.sections.${secIndex}.displayName`,
            newName,
            'Rename section'
          );
        }
      }
      labelEl.removeEventListener('blur', finish);
      labelEl.removeEventListener('keydown', onKey);
    };

    const onKey = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        labelEl.blur();
      } else if (e.key === 'Escape') {
        labelEl.contentEditable = 'false';
        // Revert to original
        this._renderRows();
      }
    };

    labelEl.addEventListener('blur', finish);
    labelEl.addEventListener('keydown', onKey);
  }

  // ── Variant popover ──

  // Phase 5 — anchor a popover/panel to a row's right edge as a floating
  // side-popover (position: fixed). Replaces the legacy "insert as sibling
  // after the row" pattern so the section-list scroll position never jumps.
  // Returns a cleanup function the caller chains into its own teardown.
  _anchorPopoverToRow(
    popover,
    rowEl,
    { width = 380, onAutoClose, closeOnStateChange = true } = {}
  ) {
    popover.classList.add('pb-sl-popover-floating');
    popover.style.position = 'fixed';
    popover.style.width = `${width}px`;
    popover.style.zIndex = 'var(--pb-z-dropdown)';
    document.body.appendChild(popover);

    const viewportPadding = 8;
    let repositionQueued = false;

    const reposition = () => {
      const safe = getSafeAreaInsets();
      const gutterTop = Math.max(viewportPadding, Number(safe.top) || 0);
      const gutterRight = Math.max(viewportPadding, Number(safe.right) || 0);
      const gutterBottom = Math.max(viewportPadding, Number(safe.bottom) || 0);
      const gutterLeft = Math.max(viewportPadding, Number(safe.left) || 0);
      const viewportHeight = getViewportHeight() || window.innerHeight || 0;

      if (isPhoneViewport()) {
        const vv = window.visualViewport;
        const viewportBottom = (Number.isFinite(vv?.offsetTop) ? vv.offsetTop : 0) + viewportHeight;
        const layoutBottomOffset = Math.max(
          0,
          (window.innerHeight || viewportHeight) - viewportBottom
        );
        const sheetMaxHeight = Math.max(168, viewportHeight - gutterTop - gutterBottom - 56);
        popover.classList.add('pb-sl-popover-sheet');
        popover.style.left = `${gutterLeft}px`;
        popover.style.right = 'auto';
        popover.style.top = 'auto';
        popover.style.bottom = `${layoutBottomOffset + gutterBottom}px`;
        popover.style.width = `${Math.max(0, (window.innerWidth || width) - gutterLeft - gutterRight)}px`;
        popover.style.maxHeight = `${sheetMaxHeight}px`;
        return;
      }

      popover.classList.remove('pb-sl-popover-sheet');
      popover.style.right = '';
      popover.style.bottom = '';
      const rect = rowEl.getBoundingClientRect();
      const maxViewportHeight = Math.max(120, viewportHeight - viewportPadding * 2);
      popover.style.maxHeight = `${maxViewportHeight}px`;
      const desiredHeight = Math.min(
        popover.scrollHeight || popover.offsetHeight || 200,
        maxViewportHeight
      );
      // Default: float to the right of the row + 12 px gap. If that would
      // overflow the viewport, flip to the left of the row.
      let left = rect.right + 12;
      if (left + width > window.innerWidth - viewportPadding) {
        left = Math.max(viewportPadding, rect.left - width - 12);
      }
      let top = rect.top;
      if (top + desiredHeight > viewportHeight - viewportPadding) {
        top = Math.max(viewportPadding, viewportHeight - desiredHeight - viewportPadding);
      }
      const availableHeight = Math.max(120, viewportHeight - top - viewportPadding);
      popover.style.left = `${left}px`;
      popover.style.top = `${top}px`;
      popover.style.maxHeight = `${availableHeight}px`;
    };

    const queueReposition = () => {
      if (repositionQueued) return;
      repositionQueued = true;
      requestAnimationFrame(() => {
        repositionQueued = false;
        if (popover.isConnected) reposition();
      });
    };
    reposition();

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(queueReposition) : null;
    resizeObserver?.observe(popover);

    // Scroll/resize/state changes invalidate the anchor — close the popover
    // rather than re-anchoring (cleaner UX, matches Linear/Figma side-popovers).
    const cb = (event) => {
      if (event?.target && popover.contains(event.target)) {
        queueReposition();
        return;
      }
      onAutoClose?.();
    };
    // Capture-phase scroll catches every scrollable ancestor, not just window.
    window.addEventListener('scroll', cb, { capture: true, passive: true });
    window.addEventListener('resize', cb);
    const viewportCleanup = onViewportChange(queueReposition);
    const onState = () => {
      if (closeOnStateChange) cb();
    };
    eventBus.on('state-changed', onState);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('scroll', cb, { capture: true });
      window.removeEventListener('resize', cb);
      viewportCleanup();
      eventBus.off('state-changed', onState);
    };
  }

  // ── SVG panel ──

  // Phase 7 — count of distinct elements with per-element overrides on this
  // section. Drives the 🎨 N badge in the row.
  _getOverridesCount(section) {
    const els = section?.overrides?.elements;
    if (!els || typeof els !== 'object') return 0;
    let count = 0;
    for (const key in els) {
      if (Object.prototype.hasOwnProperty.call(els, key)) {
        const props = els[key];
        if (props && typeof props === 'object' && Object.keys(props).length > 0) count++;
      }
    }
    return count;
  }

  _getSvgCount(section) {
    if (!section.html) return 0;
    try {
      const annotated = injectEditableAttributes(section.html);
      const parser = new DOMParser();
      const doc = parser.parseFromString(`<div>${annotated}</div>`, 'text/html');
      const editables = doc.body.firstElementChild.querySelectorAll('[data-pb-editable="svg"]');
      return editables.length;
    } catch {
      return 0;
    }
  }

  _getSvgEditables(section) {
    if (!section.html) return [];
    try {
      const annotated = injectEditableAttributes(section.html);
      const parser = new DOMParser();
      const doc = parser.parseFromString(`<div>${annotated}</div>`, 'text/html');
      const allEditables = doc.body.firstElementChild.querySelectorAll('[data-pb-editable]');
      const result = [];
      for (let i = 0; i < allEditables.length; i++) {
        if (allEditables[i].getAttribute('data-pb-editable') === 'svg') {
          result.push({
            editableIndex: i,
            eid: allEditables[i].getAttribute('data-pb-eid') || '',
            targetRef: {
              domain: 'dom',
              eid: allEditables[i].getAttribute('data-pb-eid') || ''
            },
            editableType: 'svg',
            kind: allEditables[i].getAttribute('data-pb-kind') || 'icon',
            svgHtml: allEditables[i].outerHTML
          });
        }
      }
      return result;
    } catch {
      return [];
    }
  }

  _installMediaPanelDismiss(panel, rowEl) {
    const onDocPointerDown = (e) => {
      const target = e.target;
      if (!target) return;
      if (panel.contains(target)) return;
      if (rowEl?.contains?.(target)) return;
      this._closeMediaPanel();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        this._closeMediaPanel();
      }
    };

    let listenersInstalled = false;
    const listenerFrame = requestAnimationFrame(() => {
      if (!this._activeMediaPanel || this._activeMediaPanel.el !== panel) return;
      document.addEventListener('mousedown', onDocPointerDown, true);
      document.addEventListener('touchstart', onDocPointerDown, true);
      document.addEventListener('keydown', onKey);
      listenersInstalled = true;
    });

    return () => {
      if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(listenerFrame);
      if (!listenersInstalled) return;
      document.removeEventListener('mousedown', onDocPointerDown, true);
      document.removeEventListener('touchstart', onDocPointerDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }

  _closeMediaPanel({ immediate = false } = {}) {
    if (!this._activeMediaPanel) return;

    const panelState = this._activeMediaPanel;
    this._activeMediaPanel = null;
    panelState.rowEl.classList.remove('pb-sl-media-open');
    panelState.gridNavCleanup?.();
    panelState.floatingCleanup?.();
    panelState.dismissCleanup?.();

    // Reset aria-expanded on the badge that opened this panel.
    const badgeClass = panelState.type === 'svg' ? '.pb-sl-svg-badge' : '.pb-sl-img-badge';
    const badge = panelState.rowEl.querySelector(badgeClass);
    if (badge) badge.setAttribute('aria-expanded', 'false');

    if (immediate || prefersReducedMotion()) {
      panelState.el.remove();
      return;
    }

    animateOut(panelState.el).then(() => {
      if (panelState.el.isConnected) panelState.el.remove();
    });
  }

  _toggleSvgPanel(rowEl, section) {
    this._collapseToRow(section.instanceId); // single-target rule (16A1)
    if (
      this._activeMediaPanel &&
      this._activeMediaPanel.instanceId === section.instanceId &&
      this._activeMediaPanel.type === 'svg'
    ) {
      this._closeMediaPanel();
      return;
    }
    this._closeMediaPanel({ immediate: true });

    const svgs = this._getSvgEditables(section);
    if (svgs.length === 0) return;

    const panel = document.createElement('div');
    panel.className = 'pb-sl-svg-panel';

    const header = document.createElement('div');
    header.className = 'pb-sl-media-panel-header';

    const title = document.createElement('div');
    title.className = 'pb-sl-svg-panel-title';
    title.textContent = `SVG Assets (${svgs.length})`;
    header.appendChild(title);

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'pb-sl-media-panel-close';
    closeBtn.setAttribute('aria-label', 'Close SVG assets panel');
    closeBtn.textContent = '\u00d7';
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this._closeMediaPanel();
    });
    header.appendChild(closeBtn);
    panel.appendChild(header);

    for (const svg of svgs) {
      const item = document.createElement('div');
      item.className = 'pb-sl-svg-item';
      item.setAttribute('tabindex', '0');
      item.setAttribute('role', 'button');

      const preview = document.createElement('div');
      preview.className = 'pb-sl-svg-preview pb-checker';
      const tempDoc = new DOMParser().parseFromString(svg.svgHtml, 'text/html');
      const svgEl = tempDoc.body.querySelector('svg');
      if (svgEl) {
        svgEl.removeAttribute('data-pb-editable');
        preview.innerHTML = svgEl.outerHTML;
      }
      item.appendChild(preview);

      const label = document.createElement('span');
      label.className = 'pb-sl-svg-item-label';
      label.textContent = `SVG #${svg.editableIndex + 1}`;
      label.dataset.tooltip = label.textContent;
      item.appendChild(label);

      const replaceBtn = document.createElement('button');
      replaceBtn.className = 'pb-sl-svg-replace';
      replaceBtn.textContent = 'Replace';
      replaceBtn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const originalLabel = replaceBtn.textContent;
        replaceBtn.disabled = true;
        replaceBtn.setAttribute('aria-busy', 'true');
        replaceBtn.textContent = 'Loading…';
        const target = {
          instanceId: section.instanceId,
          editableIndex: svg.editableIndex,
          eid: svg.eid,
          targetRef: svg.targetRef,
          editableType: 'svg',
          kind: svg.kind
        };
        try {
          await openReplaceDialog({ target });
        } finally {
          replaceBtn.disabled = false;
          replaceBtn.removeAttribute('aria-busy');
          replaceBtn.textContent = originalLabel;
        }
      });
      item.appendChild(replaceBtn);
      item.addEventListener('click', () => replaceBtn.click());

      const swapBtn = document.createElement('button');
      swapBtn.className = 'pb-sl-svg-replace';
      swapBtn.textContent = 'To IMG';
      swapBtn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const originalLabel = swapBtn.textContent;
        swapBtn.disabled = true;
        swapBtn.setAttribute('aria-busy', 'true');
        swapBtn.textContent = 'Swapping…';
        setActiveSvgEdit({
          instanceId: section.instanceId,
          editableIndex: svg.editableIndex,
          editableType: 'svg'
        });
        try {
          await swapSvgToImage();
        } finally {
          swapBtn.disabled = false;
          swapBtn.removeAttribute('aria-busy');
          swapBtn.textContent = originalLabel;
        }
      });
      item.appendChild(swapBtn);

      item.addEventListener('pointerenter', (e) => {
        if (e.pointerType && e.pointerType !== 'mouse') return;
        eventBus.emit('section:hover', { instanceId: section.instanceId });
      });
      item.addEventListener('pointerleave', (e) => {
        if (e.pointerType && e.pointerType !== 'mouse') return;
        eventBus.emit('section:hover', { instanceId: null });
      });

      panel.appendChild(item);
    }

    // Phase 5 — float beside the row instead of pushing rows down.
    const floatingCleanup = this._anchorPopoverToRow(panel, rowEl, {
      width: 320,
      onAutoClose: () => this._closeMediaPanel()
    });
    animateIn(panel);
    const gridNavCleanup = gridKeyNav(panel, '.pb-sl-svg-item', {
      onSelect: (el) => el.click()
    });
    const dismissCleanup = this._installMediaPanelDismiss(panel, rowEl);
    rowEl.classList.add('pb-sl-media-open');
    const svgBadge = rowEl.querySelector('.pb-sl-svg-badge');
    if (svgBadge) svgBadge.setAttribute('aria-expanded', 'true');
    this._activeMediaPanel = {
      el: panel,
      rowEl,
      instanceId: section.instanceId,
      type: 'svg',
      gridNavCleanup,
      floatingCleanup,
      dismissCleanup
    };
  }

  _getImageEditables(section) {
    const result = [];
    if (!section.html && !section.css) return result;

    try {
      if (section.html) {
        const annotated = injectEditableAttributes(section.html);
        const parser = new DOMParser();
        const doc = parser.parseFromString(`<div>${annotated}</div>`, 'text/html');
        const allEditables = doc.body.firstElementChild.querySelectorAll('[data-pb-editable]');
        for (let i = 0; i < allEditables.length; i++) {
          if (allEditables[i].getAttribute('data-pb-editable') === 'image') {
            result.push({
              editableType: 'image',
              editableIndex: i,
              eid: allEditables[i].getAttribute('data-pb-eid') || '',
              targetRef: {
                domain: 'dom',
                eid: allEditables[i].getAttribute('data-pb-eid') || ''
              },
              kind: allEditables[i].getAttribute('data-pb-kind') || 'photo',
              imgSrc: allEditables[i].getAttribute('src') || ''
            });
          }
        }
      }

      const cssBackgrounds = extractSectionCssBackgroundImages(section.css, section.sectionId);
      for (const bg of cssBackgrounds) {
        result.push({
          editableType: 'css-background',
          kind: 'background',
          cssBgIndex: bg.index,
          targetRef: {
            domain: 'section-css',
            key: bg.key,
            locator: bg.locator,
            expectedFingerprint: bg.expectedFingerprint
          },
          provenance: bg.provenance,
          imgSrc: bg.url,
          selector: bg.selector || '',
          media: bg.media || '',
          breakpoint: bg.breakpoint || ''
        });
      }

      return result;
    } catch {
      return result;
    }
  }

  _getImageCount(section) {
    return this._getImageEditables(section).length;
  }

  _toggleImagePanel(rowEl, section) {
    this._collapseToRow(section.instanceId); // single-target rule (16A1)
    if (
      this._activeMediaPanel &&
      this._activeMediaPanel.instanceId === section.instanceId &&
      this._activeMediaPanel.type === 'img'
    ) {
      this._closeMediaPanel();
      return;
    }
    this._closeMediaPanel({ immediate: true });

    const images = this._getImageEditables(section);
    if (images.length === 0) return;

    const panel = document.createElement('div');
    panel.className = 'pb-sl-img-panel';

    const header = document.createElement('div');
    header.className = 'pb-sl-media-panel-header';

    const title = document.createElement('div');
    title.className = 'pb-sl-svg-panel-title';
    title.textContent = `Images (${images.length})`;
    header.appendChild(title);

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'pb-sl-media-panel-close';
    closeBtn.setAttribute('aria-label', 'Close images panel');
    closeBtn.textContent = '\u00d7';
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this._closeMediaPanel();
    });
    header.appendChild(closeBtn);
    panel.appendChild(header);

    for (const img of images) {
      const item = document.createElement('div');
      item.className = 'pb-sl-img-item';
      item.setAttribute('tabindex', '0');
      item.setAttribute('role', 'button');

      const preview = document.createElement('div');
      preview.className = 'pb-sl-img-preview';
      const src = img.imgSrc;
      const bareAssetId =
        src && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(src)
          ? src
          : '';
      if (src && !src.startsWith('pb-asset://') && !bareAssetId) {
        preview.innerHTML = `<img src="${src}" alt="" loading="lazy" />`;
      } else if (src && (src.startsWith('pb-asset://') || bareAssetId)) {
        const assetId = bareAssetId || src.replace('pb-asset://', '');
        const placeholder = document.createElement('div');
        placeholder.className = 'pb-sl-img-placeholder';
        placeholder.textContent = 'IMG';
        preview.appendChild(placeholder);
        store.assets
          .getBlobUrl(assetId)
          .then((blobUrl) => {
            preview.innerHTML = `<img src="${blobUrl}" alt="" />`;
          })
          .catch((err) => {
            preview.innerHTML = '<div class="pb-sl-img-placeholder">ERR</div>';
            reportError('SectionList', err, {
              user: 'silent',
              detail: `image preview blob load failed for ${assetId}`
            });
          });
      } else {
        const placeholder = document.createElement('div');
        placeholder.className = 'pb-sl-img-placeholder';
        placeholder.textContent = 'IMG';
        preview.appendChild(placeholder);
      }
      item.appendChild(preview);

      const label = document.createElement('span');
      label.className = 'pb-sl-svg-item-label';
      if (img.editableType === 'css-background') {
        const bgHints = [];
        if (img.breakpoint) bgHints.push(img.breakpoint);
        if (/::?before/i.test(img.selector || '')) bgHints.push('before');
        if (/::?after/i.test(img.selector || '')) bgHints.push('after');
        if (/\]\s+[^{]+/i.test(img.selector || '')) bgHints.push('nested');
        label.textContent = `Background #${(img.cssBgIndex || 0) + 1}${bgHints.length ? ` (${bgHints.join(', ')})` : ''}`;
        label.dataset.tooltip = [label.textContent, img.media, img.selector]
          .filter(Boolean)
          .join(' | ');
      } else {
        label.textContent = `Image #${img.editableIndex + 1}`;
        label.dataset.tooltip = label.textContent;
      }
      item.appendChild(label);

      const replBtn = document.createElement('button');
      replBtn.className = 'pb-sl-svg-replace';
      replBtn.textContent = 'Replace';
      replBtn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const originalLabel = replBtn.textContent;
        replBtn.disabled = true;
        replBtn.setAttribute('aria-busy', 'true');
        replBtn.textContent = 'Replacing…';
        const target =
          img.editableType === 'css-background'
            ? {
                instanceId: section.instanceId,
                editableType: 'css-background',
                kind: 'background',
                cssBgIndex: img.cssBgIndex,
                targetRef: img.targetRef,
                provenance: img.provenance
              }
            : {
                instanceId: section.instanceId,
                editableIndex: img.editableIndex,
                eid: img.eid,
                targetRef: img.targetRef,
                editableType: 'image',
                kind: img.kind
              };
        try {
          await openReplaceDialog({ target });
        } finally {
          replBtn.disabled = false;
          replBtn.removeAttribute('aria-busy');
          replBtn.textContent = originalLabel;
        }
      });
      item.appendChild(replBtn);
      item.addEventListener('click', () => replBtn.click());

      if (img.editableType !== 'css-background') {
        const swapBtn = document.createElement('button');
        swapBtn.className = 'pb-sl-svg-replace';
        swapBtn.textContent = 'To SVG';
        swapBtn.addEventListener('click', async (e) => {
          e.stopPropagation();
          const originalLabel = swapBtn.textContent;
          swapBtn.disabled = true;
          swapBtn.setAttribute('aria-busy', 'true');
          swapBtn.textContent = 'Loading…';
          setActiveImageEdit({
            instanceId: section.instanceId,
            editableIndex: img.editableIndex,
            editableType: 'image'
          });
          try {
            await openSvgLibraryForSwap();
          } finally {
            swapBtn.disabled = false;
            swapBtn.removeAttribute('aria-busy');
            swapBtn.textContent = originalLabel;
          }
        });
        item.appendChild(swapBtn);
      }

      item.addEventListener('pointerenter', (e) => {
        if (e.pointerType && e.pointerType !== 'mouse') return;
        eventBus.emit('section:hover', { instanceId: section.instanceId });
      });
      item.addEventListener('pointerleave', (e) => {
        if (e.pointerType && e.pointerType !== 'mouse') return;
        eventBus.emit('section:hover', { instanceId: null });
      });

      panel.appendChild(item);
    }

    // Phase 5 — float beside the row instead of pushing rows down.
    const floatingCleanup = this._anchorPopoverToRow(panel, rowEl, {
      width: 320,
      onAutoClose: () => this._closeMediaPanel()
    });
    animateIn(panel);
    const gridNavCleanup = gridKeyNav(panel, '.pb-sl-img-item', {
      onSelect: (el) => el.click()
    });
    const dismissCleanup = this._installMediaPanelDismiss(panel, rowEl);
    rowEl.classList.add('pb-sl-media-open');
    const imgBadge = rowEl.querySelector('.pb-sl-img-badge');
    if (imgBadge) imgBadge.setAttribute('aria-expanded', 'true');
    this._activeMediaPanel = {
      el: panel,
      rowEl,
      instanceId: section.instanceId,
      type: 'img',
      gridNavCleanup,
      floatingCleanup,
      dismissCleanup
    };
  }

  // ── Hover cooldown ──

  _startHoverCooldown() {
    this._hoverCooldownActive = true;
    setTimeout(() => {
      this._hoverCooldownActive = false;
    }, 200);
  }

  // ── Add Section modal ──

  /** Public entry point for command palette and glass overlay. */
  openAddModal(position = null, opts) {
    this._openAddModal(position, opts);
  }

  /** MM-3: open the mobile-menu fragment picker (same lazy modal instance). */
  async openMobileMenuPicker() {
    await this._openAddModal(null, { mode: 'fragment-picker' });
  }

  async _openAddModal(position, opts) {
    let loadingMarks = 0;
    try {
      if (!this._sections.length || !Object.keys(this._categoryMap).length) {
        this._setAddModalLoading(true);
        loadingMarks++;
        await this._load();
      }

      if (!this._addModal) {
        this._setAddModalLoading(true);
        loadingMarks++;
        const { AddSectionModal } = await import('./add-section-modal.js');
        this._addModal = new AddSectionModal(this._sections, this._categoryMap);
      }
      // PP-1: every normal open states where the section will land. Derived
      // HERE, once, so the header button, the empty-state chips, the insert
      // markers, the glass overlay and the command palette all get it without
      // reproducing the fallback; a caller may still pass a better sentence.
      // Fragment mode never has an insert position.
      let resolved = opts;
      if (opts?.mode !== 'fragment-picker' && typeof opts?.context !== 'string') {
        const state = store.getState();
        const page = state?.pages?.find?.((p) => p.id === state.activePage);
        resolved = {
          ...(opts || {}),
          context: describeInsertPosition(page?.sections || [], position, this._categoryMap)
        };
      }
      this._addModal.open(position, () => this._startHoverCooldown(), resolved);
    } finally {
      for (let i = 0; i < loadingMarks; i++) this._setAddModalLoading(false);
    }
  }
}
